import { useState, useEffect, useMemo, useRef, useImperativeHandle } from "react";
import * as React from "react";
import { Link } from "react-router-dom";
import { renderToString } from "react-dom/server";
import Toggle from "react-toggle";
import Select from "react-select";
import { Modal, Dropdown } from "react-bootstrap";
import { IntlProviderWrapper, translate } from "@/modules/shared/IntlProviderWrapper";
import {
    getScheduleStats,
    getLineStat,
    getLineShiftStat,
    getShiftStat,
    getShiftName,
    compareShiftPersonLineRecItems,
    getPeopleShortNamesMap,
    applyScheduleToShiftboardLines,
    preparePeopleSchedulingRequest,
    loadPeopleSchedulingParams,
    storePeopleSchedulingParams,
    getPersonShiftStatusIndicators,
    arePeopleSchedulesEqual,
    getRelatedPersonAssignments,
    getOrderedPersonCommentsForSlot,
    getCommentSlotIdentityFromComment,
    getScopeFromCommentSlot,
} from "@/lib/shifts";
import { unionBy } from "es-toolkit";
import {
    classNames,
    createHtmlAndDownload,
    dateFromCalendarISODateString,
    dateFromWeekAndShift,
    dayString,
    deepClone,
    fromShiftTag,
    niceNumber,
    shiftNumber,
    compareNumbers,
    roundToNearest,
    toISODateString,
    weekToDayShift,
    stopKeyDownPropagation,
    getDay,
    niceDate,
    shiftTag,
    setNestedValue,
    stringCompare,
    getLastShiftNumberOfWeek,
} from "@/lib/util";
import {
    buildExchangeListingLookup,
    getExchangeAvailabilities,
    getStatusLabel,
    isActiveExchangeListing,
} from "./workerExchange.lib";
import { getBackend } from "@/lib/backend/backend2";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import Loader from "@/modules/shared/ui/Loader";
import Tooltip from "@/modules/shared/ui/Tooltip";
import MultiSelect from "@/modules/shared/ui/forms/MultiSelect";
import PersonLineModal, { type PersonLineItem } from "./PersonLineModal";
import ConfirmationModal from "@/modules/shared/ui/ConfirmationModal";
import { getAllLinesForLineGroup, getLineGroupForUser } from "@/lib/businessLogic";
import * as Auth from "@/lib/auth";
import { PEOPLE_SCHEDULING_STRATEGY, PeopleSchedulingStrategyEnum } from "@/lib/ManufacturingConsts.generated";

import { PrintPage, PrintModal } from "./ShiftAssignmentWeekPoolPrintModal";
import local_settings, {
    FIELD_AUTOMATIC_SCHEDULING_PARAMS,
    FIELD_SHIFT_ASSIGNMENT_WEEK_POOL_PRINT,
} from "@/lib/localSettings";
import LineLayoutModal from "../lineLayout/LineLayoutModal";
import ShiftBoardCommentModal from "./ShiftBoardCommentModal";
import { Absences } from "./ShiftAssignmentWeekPoolAbsences";
import {
    fetchLineSections,
    getSectionedLines,
    toLineId,
    getSectionsViewModel,
} from "../lineLayout/lineLayoutEditor.lib";
import WorkerExchange, { WorkerExchangeModal } from "./WorkerExchange";
import { createDependencyMemo } from "./dependencyMemo";
import {
    groupAbsentPersonUuidsByShiftTag,
    buildShiftAbsenceSummary,
    groupScheduledPersonUuidsByShiftTag,
    removePeopleFromShiftSchedule,
} from "./shiftBoardAbsences";
import { PeopleSchedulingSettings } from "@/modules/pg/shared/PeopleSchedulingSettings";

import type { PersonData, ScheduleStatsPerson, ScheduleStats, PersonShiftStatusIndicator } from "@/lib/shifts";
import type { LineSection } from "../lineLayout/lineLayoutEditor.lib";
import type { PrintOptions } from "./ShiftAssignmentWeekPoolPrintModal";
import type {
    IHoliday,
    ILineData,
    ILineGroupData,
    IPeopleScheduleTemplate,
    IPeopleSchedulingParams,
    IPersonData,
    IPersonLineScore,
    IPersonLines,
    IPersonShiftComment,
    IPersonShiftCommentUpdate,
    IShiftPersonLineRec,
    IShiftPersonLineRecItem,
    IShiftExchangeListing,
    IShiftExchangeRequest,
    IPersonAbsenceInfo,
} from "@/lib/backend/manufacturing2.generated.types";
import type { ExchangeListingLookup, ExchangeListingInfo } from "./workerExchange.lib";
import type { InputActionMeta } from "@/modules/shared/ui/forms/MultiSelect";
import type {
    SelectedPersonShiftLine,
    PersonAssignment,
    CommentSlotWithExistingId,
    GetPersonCommentsFn,
} from "@/lib/shifts";

import "./ShiftAssignmentWeekPool.scss";
import "../HeatIndicatorColors.scss";

type TemplateOption = {
    value: number;
    label: string;
};

type LinePinState = {
    [line_uuid: string]: boolean;
};

type HeatParams = { dwell: number; warmup: number };
type PersonLineScoresQuery = { person_uuid?: string; line_uuid?: string };
type MultiSelectOption = {
    label: string;
    value: string;
};
type PeopleHandle = {
    scrollToPerson: (personUuid: string) => void;
};
type AssignedPersonElement = {
    personUuid: string;
    shiftTag: string;
    element: HTMLLIElement;
};

// React.MouseEvent<unknown> is used because a general React.MouseEvent<HTMLElement> didn't work
type DropdownItemMouseEvent = React.MouseEvent<unknown>;

const DEFAULT_PEOPLE_SCHEDULING_PARAMS: IPeopleSchedulingParams = {
    allow_one_competence_per_shift: false,
    allow_one_person_per_single_person_line: false,
    allow_overwork: false,
    consider_dwell_time: false,
    consider_partial_retirement: false,
    ignore_pinned_lines: false,
    prefer_fully_scheduled_people: false,
    strategy: PEOPLE_SCHEDULING_STRATEGY.default,
};

const line_title_cache: Map<string, string> = new Map();

/**
 * Updates the line title cache for the given line UUIDs.
 * @param {string[]} line_uuids - Array of line UUIDs to update titles for.
 * @returns {Promise<boolean>} - Returns true if the cache was updated, false if no line UUIDs were provided.
 */
const updateLineTitleCache = async (line_uuids: string[]): Promise<boolean> => {
    if (line_uuids.length === 0) {
        return false;
    }

    // Fetch line titles and update the cache
    const lines = await fetchLines(line_uuids);
    if (lines === null || lines.length === 0) {
        return false;
    }

    for (const line of lines) {
        line_title_cache.set(line.uuid, line.title);
    }

    return true;
};

/**
 * Fetches line titles for the given line UUIDs.
 * @param {string[]} line_uuids - Array of line UUIDs to fetch titles for.
 * @returns {Promise<ILineData[] | null>} - Returns an array of line data with titles or null if an error occurs.
 */
const fetchLines = async (line_uuids: string[]): Promise<ILineData[] | null> => {
    if (line_uuids.length === 0) {
        return [];
    }

    try {
        const { lines } = await getBackend().manufacturing.getLines({
            line_uuids,
        });

        return lines;
    } catch (error) {
        console.error("Error fetching line titles:", error);
    }

    return null;
};

/**
 * Fetches holidays for given week and year.
 * @param week - The week number.
 * @param year - The year.
 * @returns - Returns a promise of an array of holidays.
 */
const fetchHolidays = async (week: number, year: number): Promise<IHoliday[]> => {
    const startDateOfFirstShiftOfWeek = dateFromWeekAndShift(week, year, 0);
    const startDateOfLastShiftOfWeek = dateFromWeekAndShift(week, year, getLastShiftNumberOfWeek());

    const { holidays } = await getBackend().manufacturing.getHolidays({
        start_date: toISODateString(startDateOfFirstShiftOfWeek),
        end_date: toISODateString(startDateOfLastShiftOfWeek),
    });

    return holidays;
};

/**
 * Fetches all lines for the given line group.
 * @param line_group_uuid - The UUID of the line group.
 * @returns - Returns an array of line data.
 */
const fetchLineGroupLines = async (line_group_uuid: string): Promise<ILineData[]> => {
    const { lines } = await getAllLinesForLineGroup(line_group_uuid);
    return lines;
};

/**
 * Fetches pinned lines for the given people UUIDs or all people if no UUIDs are provided.
 * @param people_uuids - Array of person UUIDs.
 * @returns - Returns an array of pinned lines for each person.
 */
const fetchPeoplePinnedLines = async (people_uuids?: string[]): Promise<IPersonLines[]> => {
    const { people_lines } = await getBackend().manufacturing.getPeopleLines({ people_uuids });

    return people_lines;
};

/**
 * Fetches comments for list of people in given week of the year.
 */
const fetchPeopleComments = async (personIds: number[], week: number, year: number): Promise<IPersonShiftComment[]> => {
    const startDateOfFirstShiftOfWeek = dateFromWeekAndShift(week, year, 0);
    const startDateOfLastShiftOfWeek = dateFromWeekAndShift(week, year, getLastShiftNumberOfWeek());

    const { comments } = await getBackend().manufacturing.getPeopleShiftsComments({
        first_shift_ts: startDateOfFirstShiftOfWeek.getTime(),
        last_shift_ts: startDateOfLastShiftOfWeek.getTime(),
    });
    return comments;
};

/**
 * Fetches person-line scores for the given people UUIDs.
 * @param people_uuids - Array of people UUIDs.
 * @returns - Returns an array of scores for people per line.
 */
const fetchPersonLineScores = async (people_uuids: string[]): Promise<IPersonLineScore[]> => {
    const { person_line_scores } = await getBackend().manufacturing.getPersonLineScores({
        person_uuids: people_uuids,
    });

    return person_line_scores;
};

/**
 * Fetches HR absences for everyone in the org for the same calendar span as the shift-board week
 * (first through last shift of ISO week).
 */
const fetchPeopleAbsences = async (week: number, year: number): Promise<IPersonAbsenceInfo[]> => {
    const startDateOfFirstShiftOfWeek = dateFromWeekAndShift(week, year, 0);
    const startDateOfLastShiftOfWeek = dateFromWeekAndShift(week, year, getLastShiftNumberOfWeek());
    const { person_absences } = await getBackend().manufacturing.getPeopleAbsences({
        from: toISODateString(startDateOfFirstShiftOfWeek),
        to: toISODateString(startDateOfLastShiftOfWeek),
    });
    return person_absences;
};

/**
 * Fetches shift exchange listings for the given line group.
 */
const fetchShiftExchangeListings = async (
    line_group_uuid: string,
    week: number,
    year: number
): Promise<IShiftExchangeListing[]> => {
    const line_group_id = getLineGroupForUser(line_group_uuid)?.id ?? 0;
    if (line_group_id === 0) {
        return [];
    }

    const { my_listings } = await getBackend().manufacturing.getShiftExchangeListings({
        line_group_id,
        from_shift: shiftTag(year, week, 0),
        to_shift: shiftTag(year, week, getLastShiftNumberOfWeek()),
    });
    return my_listings;
};

/**
 * Fetches shift exchange requests for the given line group.
 */
const fetchShiftExchangeRequests = async (
    line_group_uuid: string,
    week: number,
    year: number
): Promise<IShiftExchangeRequest[]> => {
    const line_group_id = getLineGroupForUser(line_group_uuid)?.id ?? 0;
    if (line_group_id === 0) {
        return [];
    }

    const { incoming_requests } = await getBackend().manufacturing.getShiftExchangeRequests({
        line_group_id,
        from_shift: shiftTag(year, week, 0),
        to_shift: shiftTag(year, week, getLastShiftNumberOfWeek()),
    });

    return incoming_requests;
};

/**
 * Fetches line groups for the current organization.
 */
const fetchLineGroups = async (): Promise<ILineGroupData[]> => {
    const { line_groups } = await getBackend().manufacturing.getLineGroups({});
    return line_groups;
};

const comparePersonByName = (a: PersonData, b: PersonData): number => {
    return a.name.localeCompare(b.name);
};

const getTemplates = async (line_group_uuid: string): Promise<IPeopleScheduleTemplate[]> => {
    const { templates } = await getBackend().manufacturing.getPeopleScheduleTemplates({
        line_group_uuid,
    });
    templates.sort((a, b) => a.name.localeCompare(b.name));

    return templates;
};

const getAssignedPersonElementKey = (personUuid: string, shiftTag: string, lineUuid: string): string => {
    return `${shiftTag}:${lineUuid}:${personUuid}`;
};

const mapPersonData = (person: IPersonData, short_name: string): PersonData => {
    return {
        id: person.id,
        uuid: person.uuid,
        name: person.name,
        short_name,
        shift_preference: person.shift_preference,
        competences: person.competences ?? [],
        lineGroupExternalId: person.ted_id,
    };
};

const getPinActionLabel = (isPinned: boolean): string => {
    return isPinned
        ? translate("ShiftAssignmentWeekPool.unpin", "Unpin")
        : translate("ShiftAssignmentWeekPool.pin", "Pin");
};

const getCommentActionLabel = (hasComment: boolean): string => {
    return hasComment
        ? translate("common.edit_comment", "Edit comment")
        : translate("common.add_comment", "Add comment");
};

/** One-line meta for person comment tooltip: date · shift · line (only defined scope parts). */
const buildPersonCommentTooltipMetaLine = (comment: IPersonShiftComment, lineTitleForCell: string): string => {
    const parts: string[] = [niceDate(dateFromCalendarISODateString(comment.date))];
    if (comment.shift_number_day !== undefined) {
        parts.push(translate(getShiftName(comment.shift_number_day)));
    }

    if (comment.line_id !== undefined) {
        parts.push(lineTitleForCell);
    }

    return parts.join(" • ");
};

// Components

/**
 * Render person shift status indicators with tooltips (inline badges)
 */
const PersonShiftStatusIndicators = ({ indicators }: { indicators: PersonShiftStatusIndicator[] }) => {
    return (
        <ul className="shift-assignment-week-pool__person-indicators">
            {indicators.map(indicator => (
                <li key={indicator.key}>
                    <Tooltip content={indicator.description}>
                        <span className={`badge badge-pill badge-${indicator.variant}`}>{indicator.label}</span>
                    </Tooltip>
                </li>
            ))}
        </ul>
    );
};

/**
 * Render person shift status indicators as legend (with descriptions)
 */
const PersonShiftStatusIndicatorsLegend = ({ indicators }: { indicators: PersonShiftStatusIndicator[] }) => {
    return (
        <dl className="shift-assignment-week-pool__person-indicators-legend">
            {indicators.map(indicator => (
                <div key={indicator.key}>
                    <dt className={`badge badge-pill badge-${indicator.variant}`}>{indicator.label}</dt>
                    <dd>{indicator.description}</dd>
                </div>
            ))}
        </dl>
    );
};

export const ShiftAssignmentWeekPoolModal = (
    props: Props & {
        show: boolean;
        hasUnsavedChanges: boolean;
        onClose: () => void;
        onUnsavedChangesSave: () => Promise<void>;
    }
) => {
    const { show, hasUnsavedChanges: hasUnsavedChangesFromPool, onClose, onUnsavedChangesSave, pooled_lines } = props;
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [showCloseConfirmModal, setShowCloseConfirmModal] = useState<boolean>(false);
    const [showSwitchWeekConfirmModal, setShowSwitchWeekConfirmModal] = useState<boolean>(false);
    const [pendingWeekIncrement, setPendingWeekIncrement] = useState<number | null>(null);

    const handleScheduleChange = (lines: IShiftPersonLineRec[]) => {
        setHasUnsavedChanges(!arePeopleSchedulesEqual(lines, pooled_lines));
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError("");

        try {
            await onUnsavedChangesSave();
        } catch (error) {
            setError(translate("common.error_save", "An error occurred while saving data"));
            console.error("Failed to save schedule:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCloseRequest = () => {
        if (hasUnsavedChanges) {
            setShowCloseConfirmModal(true);
        } else {
            onClose();
        }
    };

    const handleCloseConfirmAccept = () => {
        setShowCloseConfirmModal(false);
        onClose();
    };

    const handleCloseConfirmCancel = () => {
        setShowCloseConfirmModal(false);
    };

    const handleWeekChange = (increment: number) => {
        if (hasUnsavedChanges) {
            setPendingWeekIncrement(increment);
            setShowSwitchWeekConfirmModal(true);
        } else {
            props.onWeekChange(increment);
        }
    };

    const handleSwitchWeekConfirmAccept = () => {
        if (pendingWeekIncrement !== null) {
            props.onWeekChange(pendingWeekIncrement);
        }

        setShowSwitchWeekConfirmModal(false);
        setPendingWeekIncrement(null);
    };

    const handleSwitchWeekConfirmCancel = () => {
        setShowSwitchWeekConfirmModal(false);
        setPendingWeekIncrement(null);
    };

    return (
        <Modal
            size={hasUnsavedChangesFromPool ? undefined : "lg"}
            className={classNames({ "shift-assignment-week-pool__modal": !hasUnsavedChangesFromPool })}
            dialogClassName={classNames({
                "modal-dialog-scrollable modal-max h-100": !hasUnsavedChangesFromPool,
            })}
            show={show}
            animation={false}
            keyboard={false}
            backdrop="static"
            onHide={handleCloseRequest}
            onKeyDown={stopKeyDownPropagation}
        >
            <Modal.Header>
                <Modal.Title>
                    {hasUnsavedChangesFromPool
                        ? translate("ShiftAssignmentWeekPool.unsaved_changes", "Unsaved changes")
                        : translate("Manufacturing.PeopleView.shifts", "Shift-board")}
                </Modal.Title>
                <button type="button" className="close" onClick={handleCloseRequest}>
                    <span>×</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                {error !== "" && <ErrorComponent className="mt-0" type="error" msg={error} />}
                {hasUnsavedChangesFromPool ? (
                    <ErrorComponent
                        className="my-0"
                        type="warning"
                        msg={translate("Shifts.please_save", "The shift schedule has changed. Please save the data.")}
                    />
                ) : (
                    <ShiftAssignmentWeekPool
                        {...props}
                        onWeekChange={handleWeekChange}
                        onScheduleChange={handleScheduleChange}
                    />
                )}
            </Modal.Body>
            {hasUnsavedChangesFromPool && (
                <Modal.Footer>
                    <button className="btn btn-outline-secondary" onClick={handleCloseRequest}>
                        {translate("common.cancel", "Cancel")}
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} autoFocus>
                        {translate("common.save", "Save")} {isSaving && <i className="fas fa-spinner fa-spin ml-1" />}
                    </button>
                </Modal.Footer>
            )}

            <ConfirmationModal
                show={showCloseConfirmModal}
                title={translate("ShiftAssignmentWeekPool.close_shift_board", "Close shift-board")}
                accept_label={translate("common.continue", "Continue")}
                cancel_label={translate("common.cancel", "Cancel")}
                dangerous={true}
                onAccept={handleCloseConfirmAccept}
                onCancel={handleCloseConfirmCancel}
            >
                <ErrorComponent
                    className="my-0"
                    type="warning"
                    msg={translate(
                        "common.unsaved_changes_warning",
                        "You have unsaved changes. If you continue, they will be lost."
                    )}
                />
            </ConfirmationModal>
            <ConfirmationModal
                show={showSwitchWeekConfirmModal}
                title={translate("ShiftAssignmentWeekPool.switch_week", "Switch week")}
                accept_label={translate("common.continue", "Continue")}
                cancel_label={translate("common.cancel", "Cancel")}
                dangerous={true}
                onAccept={handleSwitchWeekConfirmAccept}
                onCancel={handleSwitchWeekConfirmCancel}
            >
                <ErrorComponent
                    className="my-0"
                    type="warning"
                    msg={translate(
                        "common.unsaved_changes_warning",
                        "You have unsaved changes. If you continue, they will be lost."
                    )}
                />
            </ConfirmationModal>
        </Modal>
    );
};

const LoadTemplateModal = (props: {
    line_group_uuid: string;
    show: boolean;
    onClose: () => void;
    onApply: (template: IPeopleScheduleTemplate) => Promise<void>;
}) => {
    const { line_group_uuid, show, onClose, onApply } = props;

    const [error, setError] = useState<string>("");
    const [is_loading, setIsLoading] = useState<boolean>(false);
    const [templates, setTemplates] = useState<IPeopleScheduleTemplate[]>([]);
    const [selected_template_index, setSelectedTemplateIndex] = useState<number>(-1);
    const [show_delete_template_modal, setShowDeleteTemplateModal] = useState<boolean>(false);

    useEffect(() => {
        const fetchTemplates = async () => {
            if (show) {
                setError("");
                setIsLoading(true);
                try {
                    const templates = await getTemplates(line_group_uuid);
                    setTemplates(templates);
                } catch (error) {
                    setError((error as Error).message);
                }

                setIsLoading(false);
            }
        };
        fetchTemplates();
    }, [show]);

    const getTemplate = (index: number): IPeopleScheduleTemplate | null => {
        if (index >= 0 && index < templates.length) {
            return templates[index];
        }

        return null;
    };

    const getTemplateOption = (index: number): TemplateOption | null => {
        const template = getTemplate(index);

        if (template !== null) {
            return { value: index, label: template.name };
        }

        return null;
    };

    const getTemplateOptions = (): TemplateOption[] => {
        return templates.map((template, i) => ({
            value: i,
            label: template.name,
        }));
    };

    const handleTemplateSelectChange = (option: TemplateOption) => {
        setSelectedTemplateIndex(option.value);
    };

    const handleApply = () => {
        if (selected_template !== null) {
            onApply(selected_template);
            onClose();
        }
    };

    const handleTemplateDeleteClick = () => {
        setShowDeleteTemplateModal(true);
    };

    const handleTemplateDeleteAccept = async () => {
        if (selected_template !== null) {
            setShowDeleteTemplateModal(false);
            setIsLoading(true);
            try {
                await getBackend().manufacturing.deletePeopleScheduleTemplate({
                    line_group_uuid,
                    name: selected_template.name,
                });
                setTemplates(templates.filter(template => template.name !== selected_template.name));
                setSelectedTemplateIndex(-1);
            } catch (error) {
                setError((error as Error).message);
            }

            setIsLoading(false);
        }
    };

    const handleTemplateDeleteCancel = () => {
        setShowDeleteTemplateModal(false);
    };

    const selected_template = getTemplate(selected_template_index);

    return (
        <>
            <Modal show={show} onHide={onClose} animation={false} onKeyDown={stopKeyDownPropagation}>
                <Modal.Header>
                    <Modal.Title>{translate("ShiftAssignmentWeekPool.load_template", "Load template")}</Modal.Title>
                    <button type="button" className="close" onClick={onClose}>
                        <span>×</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <ErrorComponent className="mt-0" type="error" msg={error} />
                    <div className="form-row">
                        <div className="col flex-grow-1">
                            <label htmlFor="template-select">
                                <span>{translate("ShiftAssignmentWeekPool.template", "Template")}</span>
                            </label>
                            <Select
                                className="react-select react-select--sm"
                                classNamePrefix="react-select"
                                inputId="template-select"
                                isLoading={is_loading}
                                options={getTemplateOptions()}
                                value={getTemplateOption(selected_template_index)}
                                onChange={handleTemplateSelectChange}
                                autoFocus
                            />
                        </div>
                        <div className="col-auto d-flex align-items-end">
                            <button
                                className="btn btn-short btn-outline-secondary"
                                title={translate("Shifts.delete_template", "Delete template")}
                                disabled={selected_template_index < 0}
                                onClick={handleTemplateDeleteClick}
                            >
                                <i className="fas fa-trash" />
                            </button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-outline-secondary" onClick={onClose}>
                        {translate("common.cancel", "Cancel")}
                    </button>
                    <button className="btn btn-primary" disabled={selected_template_index < 0} onClick={handleApply}>
                        {translate("common.apply", "Apply")}
                    </button>
                </Modal.Footer>
            </Modal>
            <ConfirmationModal
                show={show_delete_template_modal}
                title={translate("Shifts.delete_template", "Delete template")}
                accept_label={translate("common.delete", "Delete")}
                cancel_label={translate("common.cancel", "Cancel")}
                dangerous={true}
                onAccept={handleTemplateDeleteAccept}
                onCancel={handleTemplateDeleteCancel}
            >
                {translate("Shifts.are_you_sure_delete_template", "Are you sure you want to delete this template?")}{" "}
                <strong>{selected_template && selected_template.name}</strong>
            </ConfirmationModal>
        </>
    );
};

const SaveAsTemplateModal = (props: {
    line_group_uuid: string;
    show: boolean;
    onClose: () => void;
    onSave: (name: string) => Promise<void>;
}) => {
    const { line_group_uuid, show, onClose, onSave } = props;

    const [template_name, setTemplateName] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [is_name_taken, setIsNameTaken] = useState<boolean>(false);
    const [is_loading, setIsLoading] = useState<boolean>(false);
    const [is_saving, setIsSaving] = useState<boolean>(false);
    const [templates, setTemplates] = useState<IPeopleScheduleTemplate[]>([]);

    useEffect(() => {
        const fetchTemplates = async () => {
            if (show) {
                setError("");
                setIsLoading(true);
                try {
                    const templates = await getTemplates(line_group_uuid);
                    setTemplates(templates);
                } catch (error) {
                    setError((error as Error).message);
                }

                setIsLoading(false);
            }
        };
        fetchTemplates();
    }, [show]);

    const isNameTaken = (name: string): boolean => {
        return templates.some(template => template.name === name);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.currentTarget.value;
        setTemplateName(name);
        setIsNameTaken(isNameTaken(name));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form_data = new FormData(event.currentTarget);
        const value = form_data.get("name");
        if (value && typeof value === "string") {
            setError("");
            setIsSaving(true);
            try {
                await onSave(value.trim());
                onClose();
            } catch (error) {
                setError((error as Error).message);
            }

            setIsSaving(false);
        }
    };

    return (
        <Modal
            className="shift-assignment-week-pool__save-as-template-modal"
            show={show}
            onHide={onClose}
            animation={false}
            onKeyDown={stopKeyDownPropagation}
        >
            <Modal.Header>
                <Modal.Title>{translate("Shifts.enter_template_name", "Enter template name")}</Modal.Title>
                <button type="button" className="close" onClick={onClose}>
                    <span>×</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <ErrorComponent className="mt-0" type="error" msg={error} />
                <form id="save-as-template-form" onSubmit={handleSubmit}>
                    <div className="form-group mb-0">
                        <label htmlFor="save-as-template-form-input">
                            {translate("Shifts.template_name", "Template name")}
                        </label>
                        <input
                            id="save-as-template-form-input"
                            className="form-control"
                            type="text"
                            name="name"
                            value={template_name}
                            onChange={handleNameChange}
                            autoFocus
                        />
                    </div>
                </form>
                {is_name_taken && (
                    <ErrorComponent
                        className="mb-0"
                        type="warning"
                        msg={translate(
                            "Shifts.template_already_exists",
                            "Template already exists. Are you sure you want to override it?"
                        )}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                {is_loading && <i className="fas fa-spinner fa-spin mr-auto" aria-hidden="true" />}
                <button className="btn btn-outline-secondary" onClick={onClose}>
                    {translate("common.cancel", "Cancel")}
                </button>
                <button form="save-as-template-form" className="btn btn-primary" disabled={template_name.length === 0}>
                    {translate("common.save", "Save")} {is_saving && <i className="fas fa-spinner fa-spin ml-1" />}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

const WeekPicker = (props: {
    year: number;
    week: number;
    disabled?: boolean;
    onChange: (increment: number) => void;
}) => {
    const { year, week, disabled = false, onChange } = props;
    return (
        <span className="week-picker">
            <button className="btn btn-light btn-short" onClick={() => onChange(-1)} disabled={disabled}>
                &lt;
            </button>
            <span>
                <strong>
                    {translate("common.week", "Week")} {String(week).padStart(2, "0")}
                </strong>{" "}
                / {year}
            </span>
            <button className="btn btn-light btn-short" onClick={() => onChange(+1)} disabled={disabled}>
                &gt;
            </button>
        </span>
    );
};

const StatBadge = (props: { values: number[]; state: number; unit?: string; tooltip?: React.ReactNode }) => {
    const { values, state, unit, tooltip } = props;
    return (
        <Tooltip content={tooltip}>
            <span
                className={classNames("shift-assignment-week-pool__stat-badge badge badge-pill", {
                    "badge-warning": state < 0,
                    "badge-success": state === 0,
                    "badge-danger": state > 0,
                })}
            >
                {values.map(value => niceNumber(value, 2)).join(" / ")} {unit}
            </span>
        </Tooltip>
    );
};

type HeatIndicatorProps = {
    warmup: number;
    dwell: number;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
};

const HeatIndicator = (props: HeatIndicatorProps) => {
    const { warmup, dwell, onClick } = props;

    const classWarmup = roundToNearest(warmup || 0, 0.25)
        .toString()
        .replace(".", "-");

    return (
        <span
            onClick={onClick}
            className={classNames("shift-assignment-week-pool__heat-indicator", `heat__${classWarmup}`, {
                interactive: Boolean(onClick),
            })}
        >
            {Number.isNaN(dwell) ? 0 : dwell}
        </span>
    );
};

const PinnedTooltipContent = (props: {
    person_uuid: string;
    line_uuids: string[];
    onPinChange: (person_uuid: string, line_pin_state: LinePinState) => Promise<void>;
}) => {
    const { person_uuid, line_uuids, onPinChange } = props;
    const [line_uuid_to_line_title_map, setLineUuidToLineTitleMap] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        let is_mounted = true;

        const updateLineTitles = async () => {
            const updated_line_uuid_to_line_title_map = new Map(line_uuid_to_line_title_map);
            const missing_line_uuids: string[] = [];
            for (const line_uuid of line_uuids) {
                const line_title = line_title_cache.get(line_uuid);
                if (line_title === undefined) {
                    missing_line_uuids.push(line_uuid);
                } else {
                    updated_line_uuid_to_line_title_map.set(line_uuid, line_title);
                }
            }

            if (missing_line_uuids.length === 0) {
                setLineUuidToLineTitleMap(updated_line_uuid_to_line_title_map);
                return;
            }

            // Ensure titles for all line UUIDs are available
            const is_cache_updated = await updateLineTitleCache(missing_line_uuids);
            if (is_cache_updated && is_mounted) {
                for (const line_uuid of missing_line_uuids) {
                    const title = line_title_cache.get(line_uuid);
                    if (title !== undefined) {
                        updated_line_uuid_to_line_title_map.set(line_uuid, title);
                    }
                }

                setLineUuidToLineTitleMap(updated_line_uuid_to_line_title_map);
            }
        };

        updateLineTitles();

        return () => {
            is_mounted = false;
        };
    }, [line_uuids]);

    const unpinLines = async (person_uuid: string, unpinned_line_uuids: string[]) => {
        const new_line_uuid_to_line_title_map = new Map(line_uuid_to_line_title_map);
        const line_pin_state: LinePinState = {};
        for (const unpinned_line_uuid of unpinned_line_uuids) {
            new_line_uuid_to_line_title_map.delete(unpinned_line_uuid);
            line_pin_state[unpinned_line_uuid] = false;
        }

        setLineUuidToLineTitleMap(new_line_uuid_to_line_title_map);

        await onPinChange(person_uuid, line_pin_state);
    };

    const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <div className="shift-assignment-week-pool__pinned-tooltip" key={line_uuids.length}>
            <p>
                <span>{translate("ShiftAssignmentWeekPool.pinned_lines", "Pinned lines")}</span>
                {line_uuids.length > 0 && (
                    <button
                        className={classNames("btn btn-primary btn-short", {
                            "is-loading": line_uuid_to_line_title_map.size === 0,
                        })}
                        onClick={() => unpinLines(person_uuid, line_uuids)}
                        onMouseDown={handleMouseDown}
                        disabled={line_uuid_to_line_title_map.size === 0}
                    >
                        {translate("ShiftAssignmentWeekPool.unpin_all", "Unpin all")}
                    </button>
                )}
            </p>
            <div>
                <table>
                    <tbody>
                        {line_uuids.map(line_uuid => {
                            const line_title = line_uuid_to_line_title_map.get(line_uuid);
                            return (
                                <tr key={line_uuid}>
                                    <th>
                                        <span className={classNames({ "is-loading": line_title === undefined })}>
                                            {line_title !== undefined
                                                ? line_title
                                                : (line_title_cache.get(line_uuid) ?? "N/A")}
                                        </span>
                                    </th>
                                    <td>
                                        <button
                                            className={classNames("btn btn-outline-secondary btn-short", {
                                                "is-loading": line_title === undefined,
                                            })}
                                            onClick={() => unpinLines(person_uuid, [line_uuid])}
                                            onMouseDown={handleMouseDown}
                                            disabled={line_title === undefined}
                                        >
                                            {translate("ShiftAssignmentWeekPool.unpin", "Unpin")}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

type PeopleProps = {
    lineGroupExternalId: string;
    people: PersonData[];
    people_stats: Map<string, ScheduleStatsPerson>;
    selected_line: IShiftPersonLineRec | null;
    selected_person: PersonData | null;
    selected_shift: string;
    selected_shift_group: number;
    person_line_scores: IPersonLineScore[];
    filterPerson: (person: PersonData) => boolean;
    getAvailableShifts: (personId: number) => string[];
    getPersonCompetenceLevel: (person_uuid: string, line_uuid: string) => number;
    getPersonPinnedLines: (person_uuid: string) => Set<string>;
    getExchangeListingInfo: (personId: number, shiftLineId?: number) => ExchangeListingInfo[];
    getLineGroupDetails: (external_id: string) => ILineGroupData | undefined;
    onPersonSelect: (person: PersonData | null) => void;
    onPinChange: (person_uuid: string, line_pin_state: LinePinState) => Promise<void>;
    onWorkerExchangeOffer: (personId: number) => void;
    getScoresByUuid: (person_line_scores: IPersonLineScore[], query: PersonLineScoresQuery) => Map<string, HeatParams>;
};

const People = React.forwardRef<PeopleHandle, PeopleProps>((props, ref) => {
    const {
        lineGroupExternalId,
        people,
        people_stats,
        selected_line,
        selected_person,
        selected_shift,
        selected_shift_group,
        person_line_scores,
        getAvailableShifts,
        getPersonCompetenceLevel,
        getPersonPinnedLines,
        getExchangeListingInfo,
        getLineGroupDetails,
        onPersonSelect,
        onPinChange,
        onWorkerExchangeOffer,
        getScoresByUuid,
    } = props;

    const personElementRefs = useRef(new Map<string, HTMLLIElement>());
    const [filter, setFilter] = useState<string>("");
    const [selected_person_uuids, setSelectedPersonUuids] = useState<string[]>([]);
    const { shift_tag: currentShiftTag } = shiftNumber(new Date());

    useImperativeHandle(ref, () => ({
        scrollToPerson: (personUuid: string) => {
            personElementRefs.current.get(personUuid)?.scrollIntoView({ block: "nearest" });
        },
    }));

    const multiSelectPeopleOptions = useMemo(() => {
        return people.filter(props.filterPerson).map(person => ({
            value: person.uuid,
            label: person.name,
        }));
    }, [people, props.filterPerson]);

    const filterPerson = (person: PersonData) => {
        const filteredPeople = props.filterPerson(person);
        if (!filteredPeople) {
            return false;
        }

        // If specific people are selected, only show those
        if (selected_person_uuids.length > 0) {
            return selected_person_uuids.includes(person.uuid);
        }

        // Otherwise, filter by search text
        return person.name.toLowerCase().includes(filter.toLowerCase());
    };

    const filterMultiPeopleOptions = (option: MultiSelectOption, inputValue: string) => {
        return option.label.toLowerCase().includes(inputValue.toLowerCase());
    };

    const togglePersonSelected = (person: PersonData) => {
        onPersonSelect(selected_person !== null && selected_person.uuid === person.uuid ? null : person);
    };

    const handleMultiPeopleSelectionChange = (selectedOptions?: MultiSelectOption[] | null) => {
        const uuids = selectedOptions ? selectedOptions.map((option: MultiSelectOption) => option.value) : [];
        setSelectedPersonUuids(uuids);
    };

    const handleMultiPeopleInputChange = (value: string, meta: InputActionMeta) => {
        if (meta.action === "set-value" || meta.action === "input-blur" || meta.action === "menu-close") {
            return;
        }

        setFilter(value);
    };

    const handleWorkerExchangeOffer = (event: React.MouseEvent<HTMLButtonElement>, personId: number) => {
        event.stopPropagation();
        onWorkerExchangeOffer(personId);
    };

    const scoresByPerson: Map<string, HeatParams> | null = useMemo(
        () => (selected_line ? getScoresByUuid(person_line_scores, { line_uuid: selected_line.line_uuid }) : null),
        [selected_line, person_line_scores]
    );

    return (
        <div className="shift-assignment-week-pool__people">
            <div>
                <MultiSelect
                    className="react-select react-select--sm"
                    classNamePrefix="react-select"
                    options={multiSelectPeopleOptions}
                    filterOption={filterMultiPeopleOptions}
                    placeholder={translate("ShiftAssignmentWeekPool.search_people", "Search people")}
                    value={multiSelectPeopleOptions.filter(option => selected_person_uuids.includes(option.value))}
                    inputValue={filter}
                    onChange={handleMultiPeopleSelectionChange}
                    onInputChange={handleMultiPeopleInputChange}
                    isClearable
                    show_count={true}
                    menuPortalTarget={document.body}
                />
            </div>
            <ul>
                {people.filter(filterPerson).map(person => {
                    const competence_level =
                        selected_line !== null ? getPersonCompetenceLevel(person.uuid, selected_line.line_uuid) : 0;
                    const person_stats = people_stats.get(person.uuid);
                    const person_shift_stats = person_stats?.shifts.get(selected_shift);
                    const person_shift_group_stats = person_stats?.shift_groups.get(selected_shift_group);
                    const pinned_line_uuids_set = getPersonPinnedLines(person.uuid);
                    const hasUnassignedShifts = getAvailableShifts(person.id).length > 0;
                    const isExchangedPerson = person.lineGroupExternalId !== lineGroupExternalId;
                    const exchangeListingInfos = getExchangeListingInfo(person.id).filter(
                        info => info.shiftTag >= currentShiftTag
                    );
                    const shiftExchangeListingStatus =
                        exchangeListingInfos.find(info => info.shiftTag === selected_shift)?.status ?? "";
                    const personScores = scoresByPerson?.get(person.uuid);

                    const time_unit = person_shift_stats !== undefined ? "h" : "d";
                    let units_assigned = 0;
                    let units_max = 0;
                    let tooltip = "";
                    if (person_shift_stats !== undefined) {
                        units_assigned = person_shift_stats.hours_assigned;
                        units_max = person_shift_stats.hours_max;
                    } else if (person_shift_group_stats !== undefined) {
                        units_assigned = person_shift_group_stats.days_assigned;
                        units_max = person_shift_group_stats.days_available;
                        tooltip = `${niceNumber(person_shift_group_stats.hours_assigned, 2)} / ${niceNumber(
                            person_shift_group_stats.hours_max,
                            2
                        )} h`;
                    } else if (person_stats !== undefined) {
                        units_assigned = person_stats.days_assigned;
                        units_max = person_stats.days_available;
                        tooltip = `${niceNumber(person_stats.hours_assigned_sum, 2)} / ${niceNumber(
                            person_stats.hours_max_sum,
                            2
                        )} h`;
                    }

                    return (
                        <li
                            key={person.uuid}
                            ref={element => {
                                if (element === null) {
                                    personElementRefs.current.delete(person.uuid);
                                    return;
                                }

                                personElementRefs.current.set(person.uuid, element);
                            }}
                        >
                            <div
                                className={classNames("gap-2", {
                                    [`has-competence-level-${competence_level}`]: competence_level > 0,
                                    "is-selected":
                                        props.selected_person !== null && props.selected_person.uuid === person.uuid,
                                    "is-scheduled": units_assigned > 0,
                                    "has-actions": hasUnassignedShifts && !isExchangedPerson,
                                })}
                                onClick={() => {
                                    togglePersonSelected(person);
                                }}
                            >
                                <div className="pr-2 mr-auto">
                                    {exchangeListingInfos.length > 0 && (
                                        <Tooltip
                                            content={
                                                <div className="shift-assignment-week-pool__person-exchange-tooltip">
                                                    <p>
                                                        {translate(
                                                            "WorkerExchange.offeredForExchange",
                                                            "Offered for exchange"
                                                        )}{" "}
                                                        {shiftExchangeListingStatus && (
                                                            <span
                                                                className={classNames(
                                                                    "exchange-status",
                                                                    `exchange-status--${shiftExchangeListingStatus}`
                                                                )}
                                                            >
                                                                {getStatusLabel(shiftExchangeListingStatus)}
                                                            </span>
                                                        )}
                                                    </p>
                                                    {!shiftExchangeListingStatus && (
                                                        <table className="table table-sm table-hover">
                                                            <tbody>
                                                                {exchangeListingInfos.map(info => {
                                                                    const { shift_day, ts_start } = shiftNumber(
                                                                        fromShiftTag(info.shiftTag)
                                                                    );
                                                                    return (
                                                                        <tr key={info.shiftTag}>
                                                                            <td>{niceDate(new Date(ts_start))}</td>
                                                                            <td>
                                                                                {translate(getShiftName(shift_day))}
                                                                            </td>
                                                                            <td>
                                                                                <span
                                                                                    className={classNames(
                                                                                        "exchange-status",
                                                                                        `exchange-status--${info.status}`
                                                                                    )}
                                                                                >
                                                                                    {getStatusLabel(info.status)}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                </div>
                                            }
                                        >
                                            <i
                                                className={classNames("exchange-status fas fa-share-alt", {
                                                    [`exchange-status--${shiftExchangeListingStatus}`]:
                                                        shiftExchangeListingStatus !== "",
                                                })}
                                            />
                                        </Tooltip>
                                    )}
                                    {isExchangedPerson && (
                                        <Tooltip
                                            content={
                                                <span>
                                                    {translate("common.linegroup", "Line-group")}:{" "}
                                                    <strong>
                                                        {getLineGroupDetails(person.lineGroupExternalId)?.title ||
                                                            "N/A"}
                                                    </strong>
                                                </span>
                                            }
                                        >
                                            <i className="fas fa-exchange-alt exchange-status exchange-status--confirmed" />
                                        </Tooltip>
                                    )}
                                    <span>{person.name}</span>
                                </div>
                                {selected_person === null && personScores && (
                                    <HeatIndicator dwell={personScores.dwell} warmup={personScores.warmup} />
                                )}
                                <div className="info">
                                    {pinned_line_uuids_set.size > 0 &&
                                        (Auth.roleCanEditShiftBoard() ? (
                                            <Tooltip
                                                content_key={String(pinned_line_uuids_set.size)}
                                                content={
                                                    <PinnedTooltipContent
                                                        person_uuid={person.uuid}
                                                        line_uuids={Array.from(pinned_line_uuids_set)}
                                                        onPinChange={onPinChange}
                                                    />
                                                }
                                            >
                                                <i className="fas fa-thumbtack pr-2_" />
                                            </Tooltip>
                                        ) : (
                                            <i className="fas fa-thumbtack pr-2_" />
                                        ))}
                                    {person_stats !== undefined && (
                                        <StatBadge
                                            values={[units_assigned, units_max]}
                                            state={compareNumbers(units_assigned, units_max)}
                                            unit={time_unit}
                                            tooltip={tooltip}
                                        />
                                    )}
                                </div>
                                {Auth.roleCanEditShiftBoard() && hasUnassignedShifts && !isExchangedPerson && (
                                    <div className="actions">
                                        <Tooltip
                                            content={translate("WorkerExchange.offerForExchange", "Offer for exchange")}
                                        >
                                            <button
                                                className="btn btn-flat-light btn-short btn-offer-worker p-0 ml-1"
                                                onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                                                    handleWorkerExchangeOffer(event, person.id)
                                                }
                                            >
                                                <i className="fas fa-share-square" />
                                            </button>
                                        </Tooltip>
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
});

const Selection = (props: {
    selected_line: IShiftPersonLineRec | null;
    selected_person: PersonData | null;
    selected_shift: string;
    selected_shift_group: number;
    onLineSelect: (line: IShiftPersonLineRec | null) => void;
    onPersonSelect: (person: PersonData | null) => void;
    onShiftSelect: (shift: string) => void;
    onShiftGroupSelect: (shift_group: number) => void;
}) => {
    const {
        selected_line,
        selected_person,
        selected_shift,
        selected_shift_group,
        onLineSelect,
        onPersonSelect,
        onShiftSelect,
        onShiftGroupSelect,
    } = props;
    const is_shift_selected = selected_shift !== "";
    const is_shift_group_selected = selected_shift_group >= 0;
    const is_line_selected = selected_line !== null;
    const is_person_selected = selected_person !== null;
    const selection_count =
        0 +
        Number(is_line_selected) +
        Number(is_person_selected) +
        Number(is_shift_selected) +
        Number(is_shift_group_selected);

    const getShiftLabel = (shift_group: number, shift_tag: string): string => {
        if (shift_group >= 0) {
            return translate(getShiftName(shift_group));
        }

        const date = fromShiftTag(shift_tag);
        if (isNaN(date.getTime())) {
            return "";
        }

        const shift_number = shiftNumber(date);
        return (
            `${translate(getShiftName(shift_number.shift_day))} - ` +
            `${dayString(getDay(date))}, ` +
            `${translate(`common.month${date.getMonth()}`).slice(0, 3)} ${date.getDate()}`
        );
    };

    const clearLine = () => {
        onLineSelect(null);
    };

    const clearPerson = () => {
        onPersonSelect(null);
    };

    const clearShift = () => {
        onShiftSelect("");
        onShiftGroupSelect(-1);
    };

    const handleClearAll = () => {
        clearLine();
        clearPerson();
        clearShift();
    };

    return (
        <div className="shift-assignment-week-pool__selection">
            <div>
                {is_person_selected && (
                    <span className="badge">
                        <span>{translate("common.person", "Person")}:</span> <strong>{selected_person?.name}</strong>
                        <button className="btn btn-outline-danger" onClick={clearPerson}>
                            <i className="fas fa-times" />
                        </button>
                    </span>
                )}
                {is_line_selected && (
                    <span className="badge">
                        <span>{translate("common.line", "Line")}:</span> <strong>{selected_line?.line_title}</strong>
                        <button className="btn btn-outline-danger" onClick={clearLine}>
                            <i className="fas fa-times" />
                        </button>
                    </span>
                )}
                {(is_shift_group_selected || is_shift_selected) && (
                    <span className="badge">
                        <span>{translate("common.shift", "Shift")}:</span>{" "}
                        <strong>{getShiftLabel(selected_shift_group, selected_shift)}</strong>
                        <button className="btn btn-outline-danger" onClick={clearShift}>
                            <i className="fas fa-times" />
                        </button>
                    </span>
                )}
                {selection_count > 1 && (
                    <button
                        className="btn btn-flat-light_ btn-outline-secondary btn-short"
                        title={translate("common.clear", "Clear")}
                        onClick={handleClearAll}
                    >
                        <i className="fas fa-trash" />
                    </button>
                )}
            </div>
        </div>
    );
};

const Settings = (props: {
    disabled?: boolean;
    hide_fully_scheduled_people: boolean;
    hide_partially_scheduled_people: boolean;
    hide_scheduled_lines: boolean;
    show_unavailable_people: boolean;
    onHideFullyScheduledPeopleChange: () => void;
    onHidePartiallyScheduledPeopleChange: () => void;
    onHideScheduledLinesChange: () => void;
    onLoadTemplateClick: () => void;
    onPrintClick: () => void;
    onSaveAsTemplateClick: () => void;
    onEditLineGroupsClick: () => void;
    onShowPersonLineScoresEditorClick?: () => void;
    onShowUnavailablePeopleChange: () => void;
    onWorkerExchangeClick: () => void;
}) => {
    const {
        disabled = false,
        hide_fully_scheduled_people,
        hide_partially_scheduled_people,
        hide_scheduled_lines,
        show_unavailable_people,
        onHideFullyScheduledPeopleChange,
        onHidePartiallyScheduledPeopleChange,
        onHideScheduledLinesChange,
        onLoadTemplateClick,
        onPrintClick,
        onSaveAsTemplateClick,
        onShowPersonLineScoresEditorClick,
        onEditLineGroupsClick,
        onShowUnavailablePeopleChange,
        onWorkerExchangeClick,
    } = props;

    const handleSettingsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    const enabled_settings_count =
        Number(hide_fully_scheduled_people) +
        Number(hide_partially_scheduled_people) +
        Number(hide_scheduled_lines) +
        Number(show_unavailable_people);

    return (
        <div className="shift-assignment-week-pool__settings">
            <Dropdown>
                <Dropdown.Toggle
                    as="button"
                    className="btn btn-short btn-outline-secondary position-relative"
                    disabled={disabled}
                >
                    <i className="fas fa-ellipsis-v" />
                </Dropdown.Toggle>
                <Dropdown.Menu align="right">
                    <div onClick={handleSettingsMenuClick}>
                        <div className="dropdown-header px-3">{translate("common.people", "People")}</div>
                        <div className="form-group px-3 pb-2 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate("ShiftAssignmentPoolEdit.hide_fully_scheduled", "Hide fully scheduled")}
                                </span>
                                <Toggle
                                    checked={hide_fully_scheduled_people}
                                    onChange={onHideFullyScheduledPeopleChange}
                                />
                            </label>
                        </div>
                        <div className="form-group px-3 pb-2 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate(
                                        "ShiftAssignmentPoolEdit.hide_partially_scheduled",
                                        "Hide partially scheduled"
                                    )}
                                </span>
                                <Toggle
                                    checked={hide_partially_scheduled_people}
                                    onChange={onHidePartiallyScheduledPeopleChange}
                                />
                            </label>
                        </div>
                        <div className="form-group px-3 pb-2 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate("ShiftAssignmentWeekPool.show_unavailable", "Show unavailable")}
                                </span>
                                <Toggle checked={show_unavailable_people} onChange={onShowUnavailablePeopleChange} />
                            </label>
                        </div>
                        <div className="dropdown-divider" />
                        <div className="dropdown-header px-3">{translate("common.lines", "Lines")}</div>
                        <div className="form-group px-3 pb-2 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate("ShiftAssignmentPoolEdit.hide_scheduled", "Hide scheduled")}
                                </span>
                                <Toggle checked={hide_scheduled_lines} onChange={onHideScheduledLinesChange} />
                            </label>
                        </div>
                    </div>
                    <div className="dropdown-divider" />
                    {Auth.roleCanEditShiftBoard() && (
                        <>
                            <button className="dropdown-item" onClick={onWorkerExchangeClick}>
                                {translate("WorkerExchange.workerExchange.lib", "Worker Exchange")}
                            </button>
                            <div className="dropdown-divider" />
                            <button className="dropdown-item" onClick={onLoadTemplateClick}>
                                {translate("ShiftAssignmentWeekPool.load_template", "Load template")}
                            </button>
                            <button className="dropdown-item" onClick={onSaveAsTemplateClick}>
                                {translate("ShiftAssignmentWeekPool.save_as_template", "Save as template")}
                            </button>
                            <div className="dropdown-divider" />
                            <button className="dropdown-item" onClick={onEditLineGroupsClick}>
                                {translate("LineLayoutEditor.edit_line_layout", "Edit line layout")}
                            </button>
                        </>
                    )}
                    {onShowPersonLineScoresEditorClick !== undefined && (
                        <button className="dropdown-item" onClick={onShowPersonLineScoresEditorClick}>
                            {translate("ShiftAssignmentPoolEdit.scores", "Scores")}
                        </button>
                    )}
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={onPrintClick}>
                        {translate("common.print", "Print")}
                    </button>
                </Dropdown.Menu>
            </Dropdown>
            {enabled_settings_count > 0 ? <span className="badge badge-danger">{enabled_settings_count}</span> : null}
        </div>
    );
};

const AssignedPeopleList = (props: {
    line: IShiftPersonLineRec;
    shift: IShiftPersonLineRecItem;
    pool_shift: IShiftPersonLineRecItem;
    schedule_stats: ScheduleStats;
    absentPeople: PersonData[];
    selected_person: PersonData | null;
    getPersonDetails: (person_uuid: string) => PersonData | undefined;
    getPersonCompetenceLevel: (person_uuid: string, line_uuid: string) => number;
    getPersonPinnedLines: (person_uuid: string) => Set<string>;
    getPersonComments: GetPersonCommentsFn;
    onPersonShiftLineSelect: (selected: SelectedPersonShiftLine | null) => void;
    onPersonRemoveFromShift: (shift: IShiftPersonLineRecItem, person_uuid: string) => void;
    onPersonSelect: (person: PersonData | null) => void;
    onPersonScrollRequest: (personUuid: string) => void;
    onAssignedPersonElementChange: (
        personUuid: string,
        shiftTag: string,
        lineUuid: string,
        element: HTMLLIElement | null
    ) => void;
    onPinChange: (person_uuid: string, line_pin_state: LinePinState) => Promise<void>;
}) => {
    const {
        line,
        pool_shift,
        shift,
        schedule_stats,
        absentPeople,
        selected_person,
        getPersonDetails,
        getPersonPinnedLines,
        getPersonCompetenceLevel,
        getPersonComments,
        onPersonShiftLineSelect,
        onPersonRemoveFromShift,
        onPersonSelect,
        onPersonScrollRequest,
        onAssignedPersonElementChange,
        onPinChange,
    } = props;
    const pool_shift_people_uuids = new Set(pool_shift.persons.map(p => p.uuid));
    const absentPersonUuids = new Set(absentPeople.map(person => person.uuid));

    const handleSelectPersonClick = (event: React.MouseEvent<HTMLButtonElement>, person_uuid: string) => {
        event.stopPropagation();
        const person_details = getPersonDetails(person_uuid);
        if (person_details !== undefined) {
            onPersonSelect(person_details);
            onPersonScrollRequest(person_uuid);
        }
    };

    const handleRemovePersonClick = (event: DropdownItemMouseEvent, person_uuid: string) => {
        event.stopPropagation();
        onPersonRemoveFromShift(shift, person_uuid);
    };

    const handlePinPersonClick = (event: DropdownItemMouseEvent, person_uuid: string) => {
        event.stopPropagation();
        const pinned_line_uuids_set = getPersonPinnedLines(person_uuid);
        const is_pinned_line = !pinned_line_uuids_set.has(line.line_uuid);
        onPinChange(person_uuid, { [line.line_uuid]: is_pinned_line });
    };

    const handleEditPersonCommentClick = (event: DropdownItemMouseEvent, selected: SelectedPersonShiftLine) => {
        event.stopPropagation();
        onPersonShiftLineSelect(selected);
    };

    const handleMoreDropdownClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    };

    return (
        <ul className="shift-assignment-week-pool__table-people">
            {shift.persons.map(person => {
                const person_details = getPersonDetails(person.uuid);
                const isAbsent = absentPersonUuids.has(person.uuid);

                // Get status indicators for the person assigned to the shift on the given line.
                const indicators = getPersonShiftStatusIndicators(
                    person.uuid,
                    line,
                    shift,
                    pool_shift_people_uuids,
                    schedule_stats,
                    person_details,
                    getPersonCompetenceLevel,
                    isAbsent
                );

                const is_person_selected = selected_person !== null && selected_person.uuid === person.uuid;
                const pinned_line_uuids_set = getPersonPinnedLines(person.uuid);
                const is_person_pinned_to_line = pinned_line_uuids_set.has(line.line_uuid);
                const shiftDate = fromShiftTag(shift.shift_tag);
                const personCommentSlot = {
                    date: toISODateString(shiftDate),
                    shift: weekToDayShift(shift.shift_number),
                    lineId: line.line_id,
                };
                const personCommentsForSlot = getOrderedPersonCommentsForSlot(
                    person.id,
                    personCommentSlot,
                    getPersonComments
                );
                const hasPersonComment = personCommentsForSlot.length > 0;

                return (
                    <li
                        key={person.uuid}
                        ref={element => {
                            onAssignedPersonElementChange(person.uuid, shift.shift_tag, line.line_uuid, element);
                        }}
                    >
                        <div
                            className={classNames("shift-assignment-week-pool__table-person", {
                                "is-selected": is_person_selected,
                                "is-pinned": is_person_pinned_to_line,
                                "has-comment": hasPersonComment,
                            })}
                        >
                            <Tooltip content={translate("common.select", "Select")}>
                                <button
                                    className="btn btn-select"
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                        handleSelectPersonClick(event, person.uuid);
                                    }}
                                >
                                    <i className="fas fa-bullseye" />
                                </button>
                            </Tooltip>
                            <Tooltip
                                content={
                                    <div className="shift-assignment-week-pool__assigned-person-tooltip">
                                        <p>{person.name}</p>
                                        {indicators.length > 0 && (
                                            <PersonShiftStatusIndicatorsLegend indicators={indicators} />
                                        )}
                                    </div>
                                }
                                interactive={false}
                            >
                                <div className="name">
                                    <span>{person_details?.short_name || person.name}</span>
                                </div>
                            </Tooltip>
                            {indicators.length > 0 && <PersonShiftStatusIndicators indicators={indicators} />}
                            <div className="actions">
                                {hasPersonComment ? (
                                    <Tooltip
                                        content={
                                            <div className="shift-assignment-week-pool__person-comment-tooltip">
                                                <p>{translate("common.comment", "Comment")}</p>
                                                {personCommentsForSlot.map(comment => {
                                                    const scope = getScopeFromCommentSlot(
                                                        getCommentSlotIdentityFromComment(comment)
                                                    );
                                                    const metaLine = buildPersonCommentTooltipMetaLine(
                                                        comment,
                                                        line.line_title
                                                    );
                                                    return (
                                                        <div key={comment.id} className="card">
                                                            <div className="card-header">
                                                                <span
                                                                    className="flex-grow-1 text-truncate"
                                                                    title={metaLine}
                                                                >
                                                                    {metaLine}
                                                                </span>
                                                                <span className="badge badge-pill badge-secondary">
                                                                    {translate(`common.${scope}`)}
                                                                </span>
                                                            </div>
                                                            <div className="card-body">{comment.comment}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        }
                                    >
                                        <button
                                            className="btn btn-comment"
                                            disabled={!Auth.roleCanEditShiftBoard()}
                                            onClick={(event: React.MouseEvent<HTMLElement>) => {
                                                handleEditPersonCommentClick(event, {
                                                    personId: person.id,
                                                    personName: person.name,
                                                    date: toISODateString(shiftDate),
                                                    shift: weekToDayShift(shift.shift_number),
                                                    lineId: line.line_id,
                                                    lineTitle: line.line_title,
                                                });
                                            }}
                                        >
                                            <i className="fas fa-comment-dots" />
                                        </button>
                                    </Tooltip>
                                ) : null}
                                {
                                    <Tooltip content={getPinActionLabel(is_person_pinned_to_line)}>
                                        <button
                                            className="btn btn-pin"
                                            disabled={!Auth.roleCanEditShiftBoard()}
                                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                handlePinPersonClick(event, person.uuid);
                                            }}
                                        >
                                            <i className="fas fa-thumbtack" />
                                        </button>
                                    </Tooltip>
                                }
                            </div>
                            {Auth.roleCanEditShiftBoard() && (
                                <Tooltip content={translate("common.remove", "Remove")}>
                                    <button
                                        className="btn btn-remove"
                                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                            handleRemovePersonClick(event, person.uuid);
                                        }}
                                    >
                                        <i className="far fa-times-circle" />
                                    </button>
                                </Tooltip>
                            )}
                            {Auth.roleCanEditShiftBoard() && (
                                <Dropdown className="actions-dropdown" onClick={handleMoreDropdownClick}>
                                    <Dropdown.Toggle as="button" className="btn btn-more">
                                        <i className="fas fa-ellipsis-v" />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu align="right">
                                        <Dropdown.Item
                                            as="button"
                                            onClick={(event: DropdownItemMouseEvent) => {
                                                handlePinPersonClick(event, person.uuid);
                                            }}
                                        >
                                            {getPinActionLabel(is_person_pinned_to_line)}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            as="button"
                                            onClick={(event: DropdownItemMouseEvent) => {
                                                handleEditPersonCommentClick(event, {
                                                    personId: person.id,
                                                    personName: person.name,
                                                    date: toISODateString(shiftDate),
                                                    shift: weekToDayShift(shift.shift_number),
                                                    lineId: line.line_id,
                                                    lineTitle: line.line_title,
                                                });
                                            }}
                                        >
                                            {getCommentActionLabel(hasPersonComment)}
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                            as="button"
                                            onClick={(event: DropdownItemMouseEvent) => {
                                                handleRemovePersonClick(event, person.uuid);
                                            }}
                                        >
                                            {translate("common.remove", "Remove")}
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

const SchedulePeopleButton = (props: {
    enable_dropdown: boolean;
    disabled: boolean;
    is_loading: boolean;
    title: string;
    onClick: (event: DropdownItemMouseEvent, strategy: PeopleSchedulingStrategyEnum) => Promise<void>;
}) => {
    const { enable_dropdown, disabled, is_loading, title, onClick } = props;
    const buttonContent = (
        <>
            <i className="fa fa-user-plus" /> {is_loading && <i className="fas fa-spinner fa-spin mr-1 ml-1" />}
        </>
    );

    return (
        <div className="btn-group mr-1">
            {enable_dropdown ? (
                <Dropdown>
                    <Dropdown.Toggle as="button" className="btn btn-outline-secondary btn-short" disabled={disabled}>
                        <Tooltip content={title} interactive={false}>
                            <span>
                                {buttonContent}
                                <i className="fas fa-chevron-down" />
                            </span>
                        </Tooltip>
                    </Dropdown.Toggle>

                    <Dropdown.Menu align="right">
                        <Dropdown.Item
                            onClick={(event: DropdownItemMouseEvent) => {
                                onClick(event, PEOPLE_SCHEDULING_STRATEGY.default);
                            }}
                        >
                            {translate(
                                "ShiftAssignmentPoolEdit.schedule_without_history",
                                "Schedule (without history)"
                            )}
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={(event: DropdownItemMouseEvent) => {
                                onClick(event, PEOPLE_SCHEDULING_STRATEGY.more_experienced_first);
                            }}
                        >
                            {translate(
                                "ShiftAssignmentPoolEdit.schedule_more_experienced_first",
                                "Schedule more experienced first"
                            )}
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={(event: DropdownItemMouseEvent) => {
                                onClick(event, PEOPLE_SCHEDULING_STRATEGY.less_experienced_first);
                            }}
                        >
                            {translate(
                                "ShiftAssignmentPoolEdit.schedule_less_experienced_first",
                                "Schedule less experienced first"
                            )}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            ) : (
                <Tooltip content={title} interactive={false}>
                    <button
                        className="btn btn-outline-secondary btn-short"
                        disabled={disabled}
                        onClick={event => {
                            onClick(event, PEOPLE_SCHEDULING_STRATEGY.default);
                        }}
                    >
                        {buttonContent}
                    </button>
                </Tooltip>
            )}
        </div>
    );
};

const Schedule = (props: {
    line_group_uuid: string;
    pool_line: IShiftPersonLineRec;
    pooled_lines: IShiftPersonLineRec[];
    lineSections: LineSection[];
    schedule_stats: ScheduleStats;
    selected_person: PersonData | null;
    selected_line: IShiftPersonLineRec | null;
    selected_shift: string;
    selected_shift_group: number;
    automatic_scheduling_params: IPeopleSchedulingParams;
    person_line_scores: IPersonLineScore[];
    absentPeopleByShiftTag: Map<string, PersonData[]>;
    filterLine: (line: IShiftPersonLineRec) => boolean;
    getHolidaysForDate: (date: string) => string[];
    getLineDetails: (line_uuid: string) => ILineData | undefined;
    getPersonCompetenceLevel: (person_uuid: string, line_uuid: string) => number;
    getPersonDetails: (person_uuid: string) => PersonData | undefined;
    getPersonPinnedLines: (person_uuid: string) => Set<string>;
    getPersonComments: GetPersonCommentsFn;
    getExchangeListingInfo: (personId: number, shiftLineId?: number) => ExchangeListingInfo[];
    onCommentSave: (comment: string, personId: number, slots: CommentSlotWithExistingId[]) => Promise<void>;
    onLineSelect: (line: IShiftPersonLineRec | null) => void;
    onPersonRemoveFromShift: (shift: IShiftPersonLineRecItem, person_uuid: string) => void;
    onPersonSelect: (person: PersonData | null) => void;
    onPersonScrollRequest: (personUuid: string) => void;
    onPersonToggle: (shifts: IShiftPersonLineRecItem[]) => void;
    onShiftSelect: (shift: string) => void;
    onShiftGroupSelect: (shift_group: number) => void;
    onUpdateSchedule: (lines: IShiftPersonLineRec[]) => void;
    onAutomaticSchedulingParamsChange: (params: IPeopleSchedulingParams) => void;
    onPinChange: (person_uuid: string, line_pin_state: LinePinState) => Promise<void>;
    onError: (error: string) => void;
    getScoresByUuid: (person_line_scores: IPersonLineScore[], query: PersonLineScoresQuery) => Map<string, HeatParams>;
}) => {
    const {
        line_group_uuid,
        pool_line,
        pooled_lines,
        lineSections,
        person_line_scores,
        selected_line,
        selected_person,
        selected_shift,
        selected_shift_group,
        schedule_stats,
        automatic_scheduling_params,
        absentPeopleByShiftTag,
        getHolidaysForDate,
        getLineDetails,
        getPersonCompetenceLevel,
        getPersonDetails,
        getPersonPinnedLines,
        getPersonComments,
        getExchangeListingInfo,
        onCommentSave,
        onLineSelect,
        onPersonRemoveFromShift,
        onPersonSelect,
        onPersonScrollRequest,
        onPersonToggle,
        onShiftSelect,
        onShiftGroupSelect,
        onUpdateSchedule,
        onAutomaticSchedulingParamsChange,
        onPinChange,
        onError,
        getScoresByUuid,
    } = props;
    const assignedPersonElementRefs = useRef(new Map<string, AssignedPersonElement>());

    const [filter, setFilter] = useState("");
    const [selected_line_uuids, setSelectedLineUuids] = useState<string[]>([]);
    const [is_loading, setIsLoading] = useState(false);
    const [selectedPersonShiftLine, setSelectedPersonShiftLine] = useState<SelectedPersonShiftLine | null>(null);
    const [commentSaveError, setCommentSaveError] = useState<string>("");
    const [isSavingComment, setIsSavingComment] = useState(false);
    const [isAbsencesExpanded, setIsAbsencesExpanded] = useState(false);

    const poolLineShifts = useMemo(
        () => [...pool_line.shifts].sort(compareShiftPersonLineRecItems),
        [pool_line.shifts]
    );

    const scheduledPersonUuidsByShiftTag = useMemo(
        () => groupScheduledPersonUuidsByShiftTag(pooled_lines),
        [pooled_lines]
    );

    const present_shift_tags: Set<string> = new Set();
    for (const shift of poolLineShifts) {
        if (selected_person !== null && shift.persons.some(p => p.uuid === selected_person.uuid)) {
            present_shift_tags.add(shift.shift_tag);
        }
    }

    const { scheduledAbsentPeopleByShiftTag, totalAbsenceCount, scheduledAbsentPeopleCount } = useMemo(
        () => buildShiftAbsenceSummary(absentPeopleByShiftTag, scheduledPersonUuidsByShiftTag),
        [absentPeopleByShiftTag, scheduledPersonUuidsByShiftTag]
    );

    const relatedAssignments: PersonAssignment[] = useMemo(
        () => getRelatedPersonAssignments(selectedPersonShiftLine, pooled_lines),
        [selectedPersonShiftLine, pooled_lines]
    );

    const multiSelectLineOptions = useMemo(() => {
        return pooled_lines.filter(props.filterLine).map(line => ({
            value: line.line_uuid,
            label: line.line_title,
        }));
    }, [pooled_lines, props.filterLine]);

    const filterLine = (line: IShiftPersonLineRec) => {
        const filteredLines = props.filterLine(line);
        if (!filteredLines) {
            return false;
        }

        // If specific lines are selected, only show those
        if (selected_line_uuids.length > 0) {
            return selected_line_uuids.includes(line.line_uuid);
        }

        // Otherwise, filter by search text
        return line.line_title.toLowerCase().includes(filter.toLowerCase());
    };

    const filterMultiLineOptions = (option: MultiSelectOption, inputValue: string) => {
        return option.label.toLowerCase().includes(inputValue.toLowerCase());
    };

    const toggleLineSelected = (line: IShiftPersonLineRec) => {
        onLineSelect(selected_line !== null && selected_line.line_uuid === line.line_uuid ? null : line);
    };

    const toggleShiftSelected = (shift: string) => {
        const new_shift = selected_shift !== shift ? shift : "";
        onShiftSelect(new_shift);
    };

    const toggleShiftGroupSelected = (shift_group: number) => {
        const new_shift_group = selected_shift_group !== shift_group ? shift_group : -1;
        onShiftGroupSelect(new_shift_group);
    };

    const handleMultiLineSelectionChange = (selectedOptions?: MultiSelectOption[] | null) => {
        const uuids = selectedOptions ? selectedOptions.map((option: MultiSelectOption) => option.value) : [];
        setSelectedLineUuids(uuids);
    };

    const handleMultiLineInputChange = (value: string, meta: InputActionMeta) => {
        if (meta.action === "set-value" || meta.action === "input-blur" || meta.action === "menu-close") {
            return;
        }

        setFilter(value);
    };

    const handleAssignPeopleToLines = async (event: DropdownItemMouseEvent, strategy: PeopleSchedulingStrategyEnum) => {
        try {
            onError("");
            setIsLoading(true);

            const lineGroup = getLineGroupForUser(line_group_uuid);
            if (!lineGroup) {
                throw new Error("Line group not found or inaccessible");
            }

            const { lines: schedule } = await getBackend().manufacturing.assignPeopleToShiftsByLines(
                preparePeopleSchedulingRequest(
                    { ...automatic_scheduling_params, strategy },
                    lineGroup.id,
                    pooled_lines,
                    pool_line,
                    selected_shift,
                    selected_shift_group,
                    filterLine
                )
            );

            const updated_lines = applyScheduleToShiftboardLines(pooled_lines, schedule, getPersonDetails);
            onUpdateSchedule(updated_lines);
        } catch (error) {
            onError(
                translate(
                    "ShiftAssignmentWeekPool.error_while_scheduling_people",
                    "An error occurred while scheduling people"
                )
            );
            console.error("Error assigning people to lines:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearLines = () => {
        const lines: IShiftPersonLineRec[] = deepClone(pooled_lines);
        for (const line of lines) {
            if (!filterLine(line)) {
                continue;
            }

            for (const shift of line.shifts) {
                if (selected_shift !== "") {
                    if (shift.shift_tag === selected_shift) {
                        shift.persons = [];
                    }
                } else if (selected_shift_group !== -1) {
                    if (weekToDayShift(shift.shift_number) === selected_shift_group) {
                        shift.persons = [];
                    }
                } else {
                    shift.persons = [];
                }
            }
        }

        onUpdateSchedule(lines);
    };

    const handlePersonShiftLineSelect = (selected: SelectedPersonShiftLine | null) => {
        setSelectedPersonShiftLine(selected);
        setCommentSaveError("");
    };

    const handleEditCommentModalClose = () => {
        setSelectedPersonShiftLine(null);
        setCommentSaveError("");
    };

    const handleEditCommentModalApply = async (
        comment: string,
        personId: number,
        slots: CommentSlotWithExistingId[]
    ) => {
        if (slots.length === 0) {
            return;
        }

        setCommentSaveError("");
        setIsSavingComment(true);
        try {
            await onCommentSave(comment, personId, slots);
            handleEditCommentModalClose();
        } catch (err) {
            setCommentSaveError(translate("common.error_save", "An error occurred while saving data"));
            console.error("Error saving comment:", err);
        } finally {
            setIsSavingComment(false);
        }
    };

    const handleAbsencesToggle = () => {
        setIsAbsencesExpanded(isExpanded => !isExpanded);
    };

    const handleAssignedPersonElementChange = (
        personUuid: string,
        shiftTag: string,
        lineUuid: string,
        element: HTMLLIElement | null
    ) => {
        const elementKey = getAssignedPersonElementKey(personUuid, shiftTag, lineUuid);
        if (element === null) {
            assignedPersonElementRefs.current.delete(elementKey);
            return;
        }

        assignedPersonElementRefs.current.set(elementKey, {
            personUuid,
            shiftTag,
            element,
        });
    };

    const scrollAssignedPersonIntoView = (personUuid: string, shiftTag: string) => {
        for (const assignedPersonElement of assignedPersonElementRefs.current.values()) {
            if (assignedPersonElement.personUuid === personUuid && assignedPersonElement.shiftTag === shiftTag) {
                assignedPersonElement.element.scrollIntoView({ block: "center", inline: "nearest" });
                return;
            }
        }
    };

    const handleAbsentPersonSelect = (person: PersonData, shiftTag: string) => {
        const isAlreadySelected = selected_person !== null && selected_person.uuid === person.uuid;
        onPersonSelect(isAlreadySelected ? null : person);
        if (!isAlreadySelected) {
            onPersonScrollRequest(person.uuid);
            scrollAssignedPersonIntoView(person.uuid, shiftTag);
        }
    };

    const removePeopleFromShifts = (personUuidsByShiftTag: Map<string, Set<string>>) => {
        const { lines, didRemovePeople } = removePeopleFromShiftSchedule(pooled_lines, personUuidsByShiftTag);
        if (didRemovePeople) {
            onUpdateSchedule(lines);
        }
    };

    const handleClearScheduledAbsentPeople = (shiftTag: string, scheduledAbsentPeople: PersonData[]) => {
        removePeopleFromShifts(new Map([[shiftTag, new Set(scheduledAbsentPeople.map(person => person.uuid))]]));
    };

    const handleClearScheduledAbsentPeopleForWeek = () => {
        const scheduledAbsentPersonUuidsByShiftTag = new Map<string, Set<string>>();
        for (const [shiftTag, scheduledAbsentPeople] of scheduledAbsentPeopleByShiftTag) {
            if (scheduledAbsentPeople.length === 0) {
                continue;
            }

            scheduledAbsentPersonUuidsByShiftTag.set(
                shiftTag,
                new Set(scheduledAbsentPeople.map(person => person.uuid))
            );
        }

        removePeopleFromShifts(scheduledAbsentPersonUuidsByShiftTag);
    };

    const renderShiftCell = (shift_group: number) => (
        <th
            colSpan={7}
            className={classNames(`shift-group-${shift_group}`, {
                "is-selected": selected_shift_group === shift_group,
                "is-clickable": true,
            })}
        >
            <div
                className="shift-assignment-week-pool__table-cell"
                onClick={() => toggleShiftGroupSelected(shift_group)}
                role="button"
                tabIndex={0}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleShiftGroupSelected(shift_group);
                    }
                }}
            >
                {translate(getShiftName(shift_group))}
            </div>
        </th>
    );

    const sectionedLines = getSectionedLines(pooled_lines.filter(filterLine), lineSections, line =>
        toLineId(line.line_id)
    );

    const scoresByLine: Map<string, HeatParams> | null = useMemo(
        () => (selected_person ? getScoresByUuid(person_line_scores, { person_uuid: selected_person.uuid }) : null),
        [selected_person, person_line_scores]
    );

    return (
        <div className="shift-assignment-week-pool__schedule">
            <table className="shift-assignment-week-pool__table table" data-testid="shift-assignment-week-pool-table">
                <thead>
                    <tr className="shift-assignment-week-pool__tr--shifts">
                        <th className="shift-assignment-week-pool__th--line">
                            <div className="shift-assignment-week-pool__table-cell">
                                <MultiSelect
                                    className="react-select react-select--sm w-100"
                                    classNamePrefix="react-select"
                                    options={multiSelectLineOptions}
                                    filterOption={filterMultiLineOptions}
                                    placeholder={translate("ShiftAssignmentWeekPool.search_lines", "Search lines")}
                                    value={multiSelectLineOptions.filter(option =>
                                        selected_line_uuids.includes(option.value)
                                    )}
                                    inputValue={filter}
                                    onChange={handleMultiLineSelectionChange}
                                    onInputChange={handleMultiLineInputChange}
                                    isClearable
                                    show_count={true}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        </th>
                        {renderShiftCell(0)}
                        {renderShiftCell(1)}
                        {renderShiftCell(2)}
                    </tr>
                    <tr className="shift-assignment-week-pool__tr--days">
                        <th className="shift-assignment-week-pool__th--line">
                            <div className="shift-assignment-week-pool__table-cell justify-content-start">
                                <SchedulePeopleButton
                                    enable_dropdown={person_line_scores.length > 0}
                                    disabled={is_loading}
                                    is_loading={is_loading}
                                    title={translate(
                                        "ShiftAssignmentPoolEdit.suggest_people_for_all_lines",
                                        "Suggest people for all filtered lines"
                                    )}
                                    onClick={handleAssignPeopleToLines}
                                />
                                <Tooltip
                                    content={translate(
                                        "ShiftAssignmentPoolEdit.clear_all_lines",
                                        "Clear all filtered lines"
                                    )}
                                    interactive={false}
                                >
                                    <button
                                        className="btn btn-outline-secondary btn-short mr-1 ml-auto"
                                        type="button"
                                        onClick={handleClearLines}
                                    >
                                        <i className="fas fa-trash-alt" />
                                    </button>
                                </Tooltip>
                                <PeopleSchedulingSettings
                                    disabled={false}
                                    scheduling_params={automatic_scheduling_params}
                                    default_scheduling_params={DEFAULT_PEOPLE_SCHEDULING_PARAMS}
                                    onSchedulingParamsChange={onAutomaticSchedulingParamsChange}
                                />
                            </div>
                        </th>
                        {poolLineShifts.map(shift => {
                            const date = fromShiftTag(shift.shift_tag);
                            const day = getDay(date);
                            const shift_day = weekToDayShift(shift.shift_number);
                            const holidays = getHolidaysForDate(toISODateString(date));
                            return (
                                <th
                                    key={shift.shift_tag}
                                    className={classNames(`shift-group-${shift_day}`, {
                                        "is-selected": selected_shift === shift.shift_tag,
                                        "is-selected-group": selected_shift_group === shift_day,
                                        "is-holiday": holidays.length > 0,
                                    })}
                                >
                                    <Tooltip content={holidays.join(", ")}>
                                        <div
                                            className="shift-assignment-week-pool__table-cell"
                                            onClick={() => {
                                                toggleShiftSelected(shift.shift_tag);
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    toggleShiftSelected(shift.shift_tag);
                                                }
                                            }}
                                        >
                                            <span className="text-nowrap">
                                                <strong>{dayString(day).slice(0, 3)}</strong>,{" "}
                                                <span>
                                                    {date.getDate()}.{" "}
                                                    {translate(`common.month${date.getMonth()}`).slice(0, 3)}
                                                </span>
                                            </span>
                                        </div>
                                    </Tooltip>
                                </th>
                            );
                        })}
                    </tr>
                    <tr className="shift-assignment-week-pool__tr--stats">
                        <th className="shift-assignment-week-pool__th--line">
                            <div className="shift-assignment-week-pool__table-cell p-0">
                                <div className="text-muted">
                                    {translate("ShiftAssignmentPoolEdit.workers", "Workers")}
                                </div>
                                <div className="text-muted">{translate("common.lines", "Lines")}</div>
                                <div className="text-muted">
                                    {translate("ShiftAssignmentPoolEdit.work_hours", "Work hours")}
                                </div>
                            </div>
                        </th>
                        {poolLineShifts.map(shift => {
                            const shift_day = weekToDayShift(shift.shift_number);
                            const {
                                hours_assigned,
                                hours_required,
                                lines_assigned,
                                lines_required,
                                workers_assigned,
                                workers_available,
                                workers_required,
                            } = getShiftStat(schedule_stats, shift.shift_tag);
                            return (
                                <th
                                    key={shift.shift_tag}
                                    className={classNames(`shift-group-${shift_day}`, {
                                        "is-selected": selected_shift === shift.shift_tag,
                                        "is-selected-group": selected_shift_group === shift_day,
                                    })}
                                >
                                    <div className="shift-assignment-week-pool__table-cell">
                                        <StatBadge
                                            values={[workers_assigned, workers_required, workers_available]}
                                            state={compareNumbers(workers_assigned, workers_available)}
                                            tooltip={
                                                <span>
                                                    {translate(
                                                        "ShiftAssignmentPoolEdit.workers_assigned",
                                                        "workers_assigned"
                                                    )}
                                                    &nbsp;/&nbsp;
                                                    {translate(
                                                        "ShiftAssignmentPoolEdit.workers_required",
                                                        "workers_required"
                                                    )}
                                                    &nbsp;/&nbsp;
                                                    {translate("ShiftAssignmentPoolEdit.all_workers", "all_workers")}
                                                </span>
                                            }
                                        />
                                        <StatBadge
                                            values={[lines_assigned, lines_required]}
                                            state={compareNumbers(lines_assigned, lines_required)}
                                            tooltip={
                                                <span>
                                                    {translate(
                                                        "ShiftAssignmentPoolEdit.assigned_lines",
                                                        "assigned_lines"
                                                    )}
                                                    &nbsp;/&nbsp;
                                                    {translate("ShiftAssignmentPoolEdit.all_lines", "all_lines")}
                                                </span>
                                            }
                                        />
                                        <StatBadge
                                            values={[hours_assigned, hours_required]}
                                            state={compareNumbers(hours_assigned, hours_required)}
                                            tooltip={
                                                <span>
                                                    {translate(
                                                        "ShiftAssignmentPoolEdit.assigned_work_hours",
                                                        "assigned_work_hours"
                                                    )}
                                                    &nbsp;/&nbsp;
                                                    {translate(
                                                        "ShiftAssignmentPoolEdit.all_work_hours",
                                                        "all_work_hours"
                                                    )}
                                                </span>
                                            }
                                        />
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {sectionedLines.map(({ line, sectionHeader, showDividerAfter }) => {
                        const line_shifts = [...line.shifts].sort(compareShiftPersonLineRecItems);
                        const active_line_shifts = line_shifts.filter(
                            shift => shift.enabled && present_shift_tags.has(shift.shift_tag)
                        );
                        const competence_level =
                            selected_person !== null
                                ? getPersonCompetenceLevel(selected_person.uuid, line.line_uuid)
                                : 0;
                        const line_details = getLineDetails(line.line_uuid);
                        const line_factor = line_details?.factor ?? 0;
                        const line_priority = line_details?.priority ?? null;
                        const line_stats = getLineStat(schedule_stats, line.line_uuid);
                        const is_not_fully_scheduled = [...line_stats.shifts.values()].some(
                            shift => shift.hours_required > 0 && shift.workers_assigned === 0
                        );
                        const lineScores = scoresByLine?.get(line.line_uuid);
                        return (
                            <React.Fragment key={line.line_uuid}>
                                {sectionHeader && (
                                    <tr className="shift-assignment-week-pool__tr--section-header">
                                        <td>
                                            <div className="shift-assignment-week-pool__table-cell">
                                                <span>{sectionHeader.title}</span>
                                            </div>
                                        </td>
                                        <td colSpan={line_shifts.length}>
                                            <div className="shift-assignment-week-pool__table-cell" />
                                        </td>
                                    </tr>
                                )}
                                <tr
                                    className={classNames({
                                        [`has-competence-level-${competence_level}`]: competence_level > 0,
                                        "is-selected":
                                            selected_line !== null && selected_line.line_uuid === line.line_uuid,
                                    })}
                                    onClick={() => {
                                        toggleLineSelected(line);
                                    }}
                                >
                                    <td className="shift-assignment-week-pool__td--line">
                                        <Tooltip
                                            content={
                                                <div className="shift-assignment-week-pool__line-title-tooltip">
                                                    <p>{line.line_title}</p>
                                                    <div>
                                                        <table>
                                                            <tbody>
                                                                <tr>
                                                                    <th>
                                                                        {translate(
                                                                            "ShiftAssignmentWeekPool.competence_factor",
                                                                            "Competence factor"
                                                                        )}
                                                                    </th>
                                                                    <td>{niceNumber(line_factor, 2)}</td>
                                                                </tr>
                                                                {line_priority !== null && (
                                                                    <tr>
                                                                        <th>
                                                                            {translate("common.priority", "Priority")}
                                                                        </th>
                                                                        <td>{niceNumber(line_priority)}</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            }
                                        >
                                            <div
                                                className={classNames(
                                                    "shift-assignment-week-pool__table-cell",
                                                    "justify-content-center",
                                                    "gap-2",
                                                    {
                                                        "has-divider-after": showDividerAfter,
                                                    }
                                                )}
                                            >
                                                <span className="mr-auto">{line.line_title}</span>
                                                {Auth.roleCanEditShiftBoard() && (
                                                    <Link
                                                        className="settings-link"
                                                        to={"/digital-twin/resources/line/edit/" + line.line_uuid}
                                                        target="_blank"
                                                    >
                                                        <i className="fas fa-cog" />
                                                    </Link>
                                                )}
                                                {selected_line === null && lineScores && (
                                                    <HeatIndicator
                                                        dwell={lineScores.dwell}
                                                        warmup={lineScores.warmup}
                                                    />
                                                )}
                                                <StatBadge values={[]} state={is_not_fully_scheduled ? -1 : 0} />
                                            </div>
                                        </Tooltip>
                                    </td>
                                    {line_shifts.map((shift, i) => {
                                        const pool_shift = poolLineShifts[i];
                                        const absentPeople = scheduledAbsentPeopleByShiftTag.get(shift.shift_tag) ?? [];
                                        const is_present =
                                            selected_person === null || present_shift_tags.has(shift.shift_tag);
                                        const [exchangeListingInfo] = getExchangeListingInfo(
                                            selected_person?.id ?? 0,
                                            pool_shift.shift_line_id
                                        );
                                        const exchangeListingStatus = exchangeListingInfo?.status;
                                        const { hours_required, workers_assigned } = getLineShiftStat(
                                            schedule_stats,
                                            line.line_uuid,
                                            shift.shift_tag
                                        );
                                        const date = fromShiftTag(shift.shift_tag);
                                        const shift_day = weekToDayShift(shift.shift_number);
                                        const holidays = getHolidaysForDate(toISODateString(date));
                                        return (
                                            <td
                                                key={i}
                                                className={classNames(`shift-group-${shift_day}`, {
                                                    "is-absent": !is_present,
                                                    "is-present": is_present && selected_person !== null,
                                                    "is-disabled": !shift.enabled,
                                                    "is-selected": selected_shift === shift.shift_tag,
                                                    "is-selected-group": selected_shift_group === shift_day,
                                                    "is-holiday": holidays.length > 0,
                                                    [`is-offered-for-exchange exchange-status--${exchangeListingStatus}`]:
                                                        exchangeListingStatus !== undefined,
                                                })}
                                            >
                                                <div
                                                    className={classNames("shift-assignment-week-pool__table-cell", {
                                                        "has-divider-after": showDividerAfter,
                                                    })}
                                                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                        if (selected_person === null) {
                                                            return;
                                                        }

                                                        event.stopPropagation();

                                                        if (
                                                            !shift.enabled ||
                                                            isActiveExchangeListing(exchangeListingInfo)
                                                        ) {
                                                            return;
                                                        }

                                                        const is_ctrl_pressed: boolean =
                                                            event.getModifierState("Control");
                                                        const toggle_shifts =
                                                            is_ctrl_pressed && is_present
                                                                ? active_line_shifts
                                                                : [shift];
                                                        onPersonToggle(toggle_shifts);
                                                    }}
                                                >
                                                    <AssignedPeopleList
                                                        line={line}
                                                        shift={shift}
                                                        pool_shift={pool_shift}
                                                        selected_person={selected_person}
                                                        schedule_stats={schedule_stats}
                                                        absentPeople={absentPeople}
                                                        getPersonCompetenceLevel={getPersonCompetenceLevel}
                                                        getPersonDetails={getPersonDetails}
                                                        getPersonPinnedLines={getPersonPinnedLines}
                                                        getPersonComments={getPersonComments}
                                                        onPersonShiftLineSelect={handlePersonShiftLineSelect}
                                                        onPersonRemoveFromShift={onPersonRemoveFromShift}
                                                        onPersonSelect={onPersonSelect}
                                                        onPersonScrollRequest={onPersonScrollRequest}
                                                        onAssignedPersonElementChange={
                                                            handleAssignedPersonElementChange
                                                        }
                                                        onPinChange={onPinChange}
                                                    />
                                                    {shift.enabled && (
                                                        <StatBadge
                                                            values={[]}
                                                            state={workers_assigned > 0 ? 0 : -1}
                                                            tooltip={`${translate(
                                                                "ShiftAssignmentPoolEdit.work_hours",
                                                                "Work hours"
                                                            )}: ${niceNumber(hours_required, 2)}`}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </React.Fragment>
                        );
                    })}
                    <tr className="shift-assignment-week-pool__tr--spacer" aria-hidden="true">
                        <td colSpan={poolLineShifts.length + 1} />
                    </tr>
                </tbody>
                <Absences
                    poolLineShifts={poolLineShifts}
                    absentPeopleByShiftTag={absentPeopleByShiftTag}
                    scheduledPersonUuidsByShiftTag={scheduledPersonUuidsByShiftTag}
                    scheduledAbsentPeopleByShiftTag={scheduledAbsentPeopleByShiftTag}
                    totalAbsenceCount={totalAbsenceCount}
                    scheduledAbsentPeopleCount={scheduledAbsentPeopleCount}
                    isExpanded={isAbsencesExpanded}
                    canEdit={Auth.roleCanEditShiftBoard()}
                    selectedPerson={selected_person}
                    selectedShift={selected_shift}
                    selectedShiftGroup={selected_shift_group}
                    getHolidaysForDate={getHolidaysForDate}
                    onToggle={handleAbsencesToggle}
                    onAbsentPersonSelect={handleAbsentPersonSelect}
                    onClearScheduledAbsentPeople={handleClearScheduledAbsentPeople}
                    onClearScheduledAbsentPeopleForWeek={handleClearScheduledAbsentPeopleForWeek}
                />
            </table>
            <ShiftBoardCommentModal
                selectedPersonShiftLine={selectedPersonShiftLine}
                relatedAssignments={relatedAssignments}
                getPersonComments={getPersonComments}
                show={selectedPersonShiftLine !== null}
                onClose={handleEditCommentModalClose}
                onApply={handleEditCommentModalApply}
                saveError={commentSaveError}
                isSaving={isSavingComment}
            />
        </div>
    );
};

type Props = {
    plant_uuid: string;
    line_group_uuid: string;
    week: number;
    year: number;
    people: IPersonData[];
    pool_line: IShiftPersonLineRec;
    pooled_lines: IShiftPersonLineRec[];
    selected_shift: string;
    isLoading: boolean;
    getPersonCompetenceLevel: (person_uuid: string, line_uuid: string) => number;
    onScheduleDataRefresh: () => Promise<void>;
    onSave: (mergedPooledLines: IShiftPersonLineRec[]) => Promise<void>;
    onWeekChange: (increment: number) => void;
    onScheduleChange?: (lines: IShiftPersonLineRec[]) => void;
};

type State = {
    error: string;
    holidays: IHoliday[];
    lines: IShiftPersonLineRec[];
    lines_details: ILineData[];
    lineSections: LineSection[];
    lineGroups: ILineGroupData[];
    people_pinned_lines: IPersonLines[];
    peopleComments: IPersonShiftComment[];
    peopleAbsences: IPersonAbsenceInfo[];
    person_line_scores: IPersonLineScore[];
    shiftExchangeListings: IShiftExchangeListing[];
    shiftExchangeRequests: IShiftExchangeRequest[];
    automatic_scheduling_params: IPeopleSchedulingParams;
    is_loading: boolean;
    is_saving: boolean;
    selected_person: PersonData | null;
    selected_line: IShiftPersonLineRec | null;
    selected_shift: string;
    selected_shift_group: number;
    selectedWorkerExchangePersonId: number | null;
    hide_fully_scheduled_people: boolean;
    hide_partially_scheduled_people: boolean;
    hide_scheduled_lines: boolean;
    show_unavailable_people: boolean;
    show_person_line_scores_modal: boolean;
    show_load_template_modal: boolean;
    show_save_as_template_modal: boolean;
    show_print_modal: boolean;
    show_edit_line_groups_modal: boolean;
    showWorkerExchangeModal: boolean;
    saved_settings: Object;
};

class ShiftAssignmentWeekPool extends React.Component<Props, State> {
    private availablePeopleDetailsMemo = createDependencyMemo<PersonData[]>();
    private holidaysByDateMemo = createDependencyMemo<Map<string, string[]>>();
    private lineDetailsByUuidMemo = createDependencyMemo<Map<string, ILineData>>();
    private personDetailsByUuidMemo = createDependencyMemo<Map<string, PersonData>>();
    private pinnedLinesByPersonUuidMemo = createDependencyMemo<Map<string, Set<string>>>();
    private commentsByDateByPersonIdMemo = createDependencyMemo<Map<number, Map<string, IPersonShiftComment[]>>>();
    private exchangeListingLookupMemo = createDependencyMemo<ExchangeListingLookup>();
    private lineGroupsByExternalIdMemo = createDependencyMemo<Map<string, ILineGroupData>>();
    private availableShiftsByPersonIdMemo = createDependencyMemo<Map<number, string[]>>();
    private scheduleStatsMemo = createDependencyMemo<ScheduleStats>();
    private absentPeopleDetailsByShiftTagMemo = createDependencyMemo<Map<string, PersonData[]>>();

    private peopleRef = React.createRef<PeopleHandle>();

    constructor(props: Props) {
        super(props);
        const state: State = {
            error: "",
            holidays: [],
            lines: [],
            lines_details: [],
            lineGroups: [],
            people_pinned_lines: [],
            peopleComments: [],
            peopleAbsences: [],
            person_line_scores: [],
            shiftExchangeListings: [],
            shiftExchangeRequests: [],
            lineSections: [],
            automatic_scheduling_params: loadPeopleSchedulingParams(
                FIELD_AUTOMATIC_SCHEDULING_PARAMS,
                DEFAULT_PEOPLE_SCHEDULING_PARAMS
            ),
            is_loading: false,
            is_saving: false,
            selected_line: null,
            selected_person: null,
            selected_shift: props.selected_shift,
            selected_shift_group: -1,
            selectedWorkerExchangePersonId: null,
            hide_fully_scheduled_people: false,
            hide_partially_scheduled_people: false,
            hide_scheduled_lines: false,
            show_unavailable_people: false,
            show_person_line_scores_modal: false,
            show_load_template_modal: false,
            show_save_as_template_modal: false,
            show_edit_line_groups_modal: false,
            show_print_modal: false,
            showWorkerExchangeModal: false,
            saved_settings: local_settings.getParsed(FIELD_SHIFT_ASSIGNMENT_WEEK_POOL_PRINT) ?? {},
        };
        this.state = state;
    }

    async componentDidMount() {
        await this.initializeData();
    }

    async componentDidUpdate(prev_props: Props, prev_state: State) {
        if (prev_props.pooled_lines !== this.props.pooled_lines) {
            await this.refreshScheduleLinesData();
        }

        if (prev_props.people !== this.props.people) {
            await this.refreshPeopleData();
        }

        if (prev_props.week !== this.props.week || prev_props.year !== this.props.year) {
            await this.refetchWeekData();
        }

        if (prev_state.lines !== this.state.lines && this.state.lines.length > 0) {
            this.props.onScheduleChange?.(this.state.lines);
        }
    }

    /**
     * Wrapper for loading data with error handling.
     * @param fn - The function to execute.
     */
    loadWithErrorHandling = async (fn: () => Promise<void>): Promise<void> => {
        this.setState({ error: "", is_loading: true });

        try {
            // Execute the function
            await fn();
        } catch (error) {
            this.setState({
                error: translate("common.error", "An error occurred while fetching data"),
            });
            console.error("Failed to fetch data:", error);
        } finally {
            this.setState({
                is_loading: false,
            });
        }
    };

    /**
     * Initial data load - fetches everything needed on mount.
     */
    initializeData = async (): Promise<void> => {
        await this.loadWithErrorHandling(async () => {
            const { line_group_uuid, people, pooled_lines, week, year } = this.props;

            // Fetch independent data in parallel
            const [
                person_line_scores,
                lines_details,
                sections,
                people_pinned_lines,
                holidays,
                peopleComments,
                peopleAbsences,
                shiftExchangeListings,
                shiftExchangeRequests,
                lineGroups,
            ] = await Promise.all([
                fetchPersonLineScores(people.map(x => x.uuid)),
                fetchLineGroupLines(line_group_uuid),
                fetchLineSections(line_group_uuid),
                fetchPeoplePinnedLines(),
                fetchHolidays(week, year),
                fetchPeopleComments(
                    people.map(x => x.id),
                    week,
                    year
                ),
                fetchPeopleAbsences(week, year),
                fetchShiftExchangeListings(line_group_uuid, week, year),
                fetchShiftExchangeRequests(line_group_uuid, week, year),
                fetchLineGroups(),
            ]);

            const lines = this.processScheduleLines(pooled_lines, lines_details);
            const lineSections = getSectionsViewModel(sections, lines);

            this.setState({
                holidays,
                lines_details,
                lines,
                lineSections,
                people_pinned_lines,
                peopleComments,
                peopleAbsences,
                person_line_scores,
                shiftExchangeListings,
                shiftExchangeRequests,
                lineGroups,
            });
        });
    };

    /**
     * Refreshes the lines data - fetches everything needed to update the lines for the new schedule.
     */
    refreshScheduleLinesData = async (): Promise<void> => {
        await this.loadWithErrorHandling(async () => {
            const { pooled_lines } = this.props;
            const { lines_details } = this.state;

            const lines = this.processScheduleLines(pooled_lines, lines_details);

            this.setState({
                lines,
            });
        });
    };

    /**
     * Refreshes the people data - fetches everything needed to update the new list of people.
     */
    refreshPeopleData = async (): Promise<void> => {
        await this.loadWithErrorHandling(async () => {
            const { people, week, year } = this.props;

            const [peopleComments, person_line_scores] = await Promise.all([
                fetchPeopleComments(
                    people.map(person => person.id),
                    week,
                    year
                ),
                fetchPersonLineScores(people.map(person => person.uuid)),
            ]);

            this.setState({
                peopleComments,
                person_line_scores,
            });
        });
    };

    /**
     * Refreshes the week data.
     */
    refetchWeekData = async (): Promise<void> => {
        await this.loadWithErrorHandling(async () => {
            const { line_group_uuid, people, week, year } = this.props;

            const [holidays, peopleComments, peopleAbsences, shiftExchangeListings, shiftExchangeRequests] =
                await Promise.all([
                    fetchHolidays(week, year),
                    fetchPeopleComments(
                        people.map(person => person.id),
                        week,
                        year
                    ),
                    fetchPeopleAbsences(week, year),
                    fetchShiftExchangeListings(line_group_uuid, week, year),
                    fetchShiftExchangeRequests(line_group_uuid, week, year),
                ]);

            this.setState({
                holidays,
                peopleComments,
                shiftExchangeListings,
                shiftExchangeRequests,
                peopleAbsences,
            });
        });
    };

    /**
     * Refreshes the worker exchange data.
     */
    refreshWorkerExchangeData = async (): Promise<void> => {
        await this.loadWithErrorHandling(async () => {
            const { line_group_uuid, week, year } = this.props;

            const [shiftExchangeListings, shiftExchangeRequests] = await Promise.all([
                fetchShiftExchangeListings(line_group_uuid, week, year),
                fetchShiftExchangeRequests(line_group_uuid, week, year),
            ]);

            this.setState({
                shiftExchangeListings,
                shiftExchangeRequests,
            });
        });
    };

    /**
     * Processes the schedule lines:
     * - filters out lines with a factor of 0
     * - updates the line title cache
     * @param lines - The schedule lines.
     * @param lines_details - The line details.
     * @returns The processed schedule lines.
     */
    processScheduleLines = (lines: IShiftPersonLineRec[], lines_details: ILineData[]): IShiftPersonLineRec[] => {
        const lines_details_map: Map<string, ILineData> = new Map();
        for (const line_details of lines_details) {
            lines_details_map.set(line_details.uuid, line_details);

            // Update line title cache
            line_title_cache.set(line_details.uuid, line_details.title);
        }

        const filteredLines = lines.filter(line => lines_details_map.get(line.line_uuid)?.factor ?? 0 > 0);
        return deepClone(filteredLines);
    };

    getAvailablePeopleDetails = (): PersonData[] => {
        const { people, pooled_lines, pool_line } = this.props;
        const { show_unavailable_people, selected_shift, selected_shift_group } = this.state;
        return this.availablePeopleDetailsMemo.get(() => {
            const availablePeopleUuids = new Set<string>(show_unavailable_people ? people.map(x => x.uuid) : []);
            if (!show_unavailable_people) {
                const shiftTagsSet = new Set(
                    selected_shift ? [selected_shift] : this.getShiftGroupShiftTags(selected_shift_group)
                );
                for (const shift of pool_line.shifts) {
                    if (shiftTagsSet.size > 0 && !shiftTagsSet.has(shift.shift_tag)) {
                        continue;
                    }

                    for (const person of shift.persons) {
                        availablePeopleUuids.add(person.uuid);
                    }
                }
            }

            const availablePeopleDetails: PersonData[] = [];
            for (const personUuid of availablePeopleUuids) {
                const personDetails = this.getPersonDetails(personUuid);
                if (personDetails !== undefined) {
                    availablePeopleDetails.push(personDetails);
                }
            }

            availablePeopleDetails.sort(comparePersonByName);
            return availablePeopleDetails;
        }, [pooled_lines, show_unavailable_people, selected_shift, selected_shift_group, pool_line, people]);
    };

    getAbsentPeopleDetailsByShiftTag = (): Map<string, PersonData[]> => {
        const { line_group_uuid, people, pool_line } = this.props;
        const { peopleAbsences, show_unavailable_people, lines } = this.state;

        return this.absentPeopleDetailsByShiftTagMemo.get(() => {
            const lineGroup = getLineGroupForUser(line_group_uuid);

            // 1. Optimize Availability Set Creation
            const availablePersonUuids = new Set(
                people
                    .filter(person => show_unavailable_people || !lineGroup || person.ted_id === lineGroup.external_id)
                    .map(person => person.uuid)
            );

            // 2. Extract and sort pool shift tags cleanly
            const poolShiftTags = pool_line.shifts.map(shift => shift.shift_tag).sort();

            const scheduledPersonUuidsByShiftTag = groupScheduledPersonUuidsByShiftTag(lines);
            const absentPersonUuidsByShiftTag = groupAbsentPersonUuidsByShiftTag(poolShiftTags, peopleAbsences);
            const absentPeopleByShiftTag = new Map<string, PersonData[]>();

            for (const [shiftTag, personUuids] of absentPersonUuidsByShiftTag) {
                const scheduledPersonUuids = scheduledPersonUuidsByShiftTag.get(shiftTag);
                const absentPeople: PersonData[] = [];

                for (const personUuid of personUuids) {
                    // Short-circuit evaluations: check available Set first, then check scheduled Set safely
                    const isAvailable = availablePersonUuids.has(personUuid);
                    const isScheduled = scheduledPersonUuids?.has(personUuid) ?? false;

                    if (!isAvailable && !isScheduled) {
                        continue;
                    }

                    const personDetails = this.getPersonDetails(personUuid);
                    if (personDetails !== undefined) {
                        absentPeople.push(personDetails);
                    }
                }

                absentPeople.sort(comparePersonByName);
                absentPeopleByShiftTag.set(shiftTag, absentPeople);
            }

            return absentPeopleByShiftTag;
        }, [line_group_uuid, people, pool_line, lines, peopleAbsences, show_unavailable_people]);
    };

    getHolidaysForDate = (date: string): string[] => {
        const { holidays } = this.state;
        return (
            this.holidaysByDateMemo
                .get(() => {
                    const holidaysByDate = new Map<string, string[]>();
                    for (const holiday of holidays) {
                        const { date: dateKey, title } = holiday;
                        const holidayTitles = holidaysByDate.get(dateKey) ?? [];
                        holidayTitles.push(title);
                        holidaysByDate.set(dateKey, holidayTitles);
                    }

                    return holidaysByDate;
                }, [holidays])
                .get(date) ?? []
        );
    };

    getLineDetails = (lineUuid: string): ILineData | undefined => {
        const { lines_details } = this.state;
        return this.lineDetailsByUuidMemo
            .get(() => new Map(lines_details.map(line => [line.uuid, line])), [lines_details])
            .get(lineUuid);
    };

    getLineFactor = (lineUuid: string): number => {
        return this.getLineDetails(lineUuid)?.factor ?? 0;
    };

    getPersonComments = (
        personId: number,
        date: string,
        shift?: number | null,
        lineId?: number | null
    ): IPersonShiftComment[] => {
        const { peopleComments } = this.state;
        return (
            this.commentsByDateByPersonIdMemo
                .get(() => {
                    const commentsByDateByPersonId = new Map<number, Map<string, IPersonShiftComment[]>>();
                    for (const personComment of peopleComments) {
                        const commentsByDate = commentsByDateByPersonId.get(personComment.person_id) ?? new Map();
                        const comments = commentsByDate.get(personComment.date) ?? [];
                        comments.push(personComment);
                        commentsByDate.set(personComment.date, comments);
                        commentsByDateByPersonId.set(personComment.person_id, commentsByDate);
                    }

                    return commentsByDateByPersonId;
                }, [peopleComments])
                .get(personId)
                ?.get(date)
                ?.filter(
                    comment =>
                        (shift === undefined || (comment.shift_number_day ?? null) === shift) &&
                        (lineId === undefined || (comment.line_id ?? null) === lineId)
                ) ?? []
        );
    };

    getPersonDetails = (personUuid: string): PersonData | undefined => {
        const { people } = this.props;
        return this.personDetailsByUuidMemo
            .get(() => {
                const personShortNameByUuid = getPeopleShortNamesMap(people);
                const personDetailsByUuid = new Map<string, PersonData>();
                for (const person of people) {
                    personDetailsByUuid.set(
                        person.uuid,
                        mapPersonData(person, personShortNameByUuid.get(person.uuid) ?? "")
                    );
                }

                return personDetailsByUuid;
            }, [people])
            .get(personUuid);
    };

    getPersonPinnedLines = (personUuid: string): Set<string> => {
        const { people_pinned_lines } = this.state;
        return (
            this.pinnedLinesByPersonUuidMemo
                .get(
                    () => new Map(people_pinned_lines.map(ppl => [ppl.person_uuid, new Set(ppl.line_uuids)])),
                    [people_pinned_lines]
                )
                .get(personUuid) ?? new Set()
        );
    };

    getPersonLineItems = (): PersonLineItem[] => {
        const { person_line_scores, lines_details } = this.state;
        const { people } = this.props;

        // Create maps for quick lookup
        const people_map = new Map(people.map(person => [person.uuid, person]));
        const lines_map = new Map(lines_details.map(line => [line.uuid, line]));

        // Transform IPersonLineScore[] to PersonLineItem[]
        const items: PersonLineItem[] = person_line_scores
            .map(score => {
                const person = people_map.get(score.person_uuid);
                const line = lines_map.get(score.line_uuid);

                // Skip if person or line not found
                if (!person || !line) {
                    return null;
                }

                return {
                    person_name: person.name,
                    person_external_id: person.external_id,
                    line_title: line.title,
                    line_external_id: line.external_id,
                    score: score.score,
                    dwell: score.dwell,
                    warmup: score.warmup,
                };
            })
            .filter((item): item is PersonLineItem => item !== null)
            .sort((a, b) => stringCompare(a.person_name, b.person_name));

        return items;
    };

    getExchangeListingInfo = (personId: number, shiftLineId?: number): ExchangeListingInfo[] => {
        const { shiftExchangeListings, shiftExchangeRequests } = this.state;
        const exchangeListingLookup = this.exchangeListingLookupMemo.get(
            () => buildExchangeListingLookup(shiftExchangeListings, shiftExchangeRequests),
            [shiftExchangeListings, shiftExchangeRequests]
        );
        if (shiftLineId === undefined) {
            return Array.from(exchangeListingLookup.get(personId)?.values() ?? []);
        }

        const listingInfo = exchangeListingLookup.get(personId)?.get(shiftLineId);
        return listingInfo ? [listingInfo] : [];
    };

    getLineGroupDetails = (lineGroupUuid: string): ILineGroupData | undefined => {
        const { lineGroups } = this.state;
        return this.lineGroupsByExternalIdMemo
            .get(() => new Map(lineGroups.map(lineGroup => [lineGroup.external_id, lineGroup])), [lineGroups])
            .get(lineGroupUuid);
    };

    getAvailableShifts = (personId: number): string[] => {
        const { pool_line, pooled_lines, people, line_group_uuid } = this.props;
        return (
            this.availableShiftsByPersonIdMemo
                .get(() => {
                    const lineGroup = getLineGroupForUser(line_group_uuid);
                    const exchangeAvailabilities = getExchangeAvailabilities(
                        lineGroup?.external_id ?? "",
                        people,
                        pool_line,
                        pooled_lines
                    );

                    const availableShiftsByPersonId = new Map<number, string[]>();
                    for (const availability of exchangeAvailabilities) {
                        const { person, shiftTag } = availability;
                        const availableShifts = availableShiftsByPersonId.get(person.id) ?? [];
                        availableShifts.push(shiftTag);
                        availableShiftsByPersonId.set(person.id, availableShifts);
                    }

                    return availableShiftsByPersonId;
                }, [pool_line, pooled_lines, people, line_group_uuid])
                .get(personId) ?? []
        );
    };

    getScheduleStats = (): ScheduleStats => {
        const { lines, lines_details } = this.state;
        const { pool_line, people } = this.props;
        return this.scheduleStatsMemo.get(
            () =>
                getScheduleStats(
                    lines,
                    pool_line.shifts,
                    people.map(x => x.uuid),
                    this.getLineFactor
                ),
            [lines, pool_line, people, lines_details]
        );
    };

    getShiftGroupShiftTags = (shift_group: number): string[] => {
        const shift_tags: string[] = [];
        if (shift_group < 0) {
            return shift_tags;
        }

        for (const shift of this.props.pool_line.shifts) {
            const shift_day = weekToDayShift(shift.shift_number);
            if (shift_day === shift_group) {
                shift_tags.push(shift.shift_tag);
            }
        }

        return shift_tags;
    };

    isLoading = (): boolean => {
        return this.props.isLoading || this.state.is_loading;
    };

    isSaving = (): boolean => {
        return this.state.is_saving;
    };

    filterPerson = (person: PersonData): boolean => {
        const { hide_fully_scheduled_people, hide_partially_scheduled_people, selected_shift, selected_shift_group } =
            this.state;

        const person_stats = this.getScheduleStats().people.get(person.uuid);
        if (person_stats === undefined) {
            return false;
        }

        // Check if the person should be shown based on the number of assigned units and the required number of units.
        const shouldKeepPersonBasedOnAssignment = (assigned: number, required: number): boolean => {
            const diff = compareNumbers(assigned, required);

            if (hide_fully_scheduled_people && diff === 0) {
                // Person is fully scheduled and should be hidden
                return false;
            }

            if (hide_partially_scheduled_people && diff < 0 && assigned > 0) {
                // Person is partially scheduled and should be hidden
                return false;
            }

            return true;
        };

        // If a single shift is selected, filter based on hours scheduled for that shift
        if (selected_shift !== "") {
            const person_shift_stats = person_stats.shifts.get(selected_shift);
            if (person_shift_stats === undefined) {
                return false;
            }

            return shouldKeepPersonBasedOnAssignment(person_shift_stats.hours_assigned, person_shift_stats.hours_max);
        }

        // If a shift group is selected, filter based on days scheduled for that group
        if (selected_shift_group !== -1) {
            const person_shift_group_stats = person_stats.shift_groups.get(selected_shift_group);
            if (person_shift_group_stats === undefined) {
                return false;
            }

            return shouldKeepPersonBasedOnAssignment(
                person_shift_group_stats.days_assigned,
                person_shift_group_stats.days_available
            );
        }

        // When nothing is selected, check by days over the whole week
        return shouldKeepPersonBasedOnAssignment(person_stats.days_assigned, person_stats.days_available);
    };

    filterLine = (line: IShiftPersonLineRec): boolean => {
        const { hide_scheduled_lines, selected_shift } = this.state;
        const line_stats = this.getScheduleStats().lines.get(line.line_uuid);
        if (line_stats !== undefined) {
            if (selected_shift !== "") {
                const line_shift_stats = line_stats.shifts.get(selected_shift);
                if (line_shift_stats !== undefined) {
                    const { workers_assigned, hours_required } = line_shift_stats;
                    if (hide_scheduled_lines && (hours_required === 0 || workers_assigned > 0)) {
                        return false;
                    }
                }
            } else {
                if (hide_scheduled_lines) {
                    let all_shifts_scheduled = true;
                    for (const stats of line_stats.shifts.values()) {
                        if (stats.hours_required > 0 && stats.workers_assigned === 0) {
                            all_shifts_scheduled = false;
                            break;
                        }
                    }

                    if (all_shifts_scheduled) {
                        return false;
                    }
                }
            }
        }

        return true;
    };

    removePersonFromShift = (shift: IShiftPersonLineRecItem, person_uuid: string) => {
        const { lines } = this.state;
        const person_index = shift.persons.findIndex(p => p.uuid === person_uuid);
        if (person_index >= 0) {
            shift.persons.splice(person_index, 1);
        }

        this.setState({
            lines: [...lines],
        });
    };

    toggleSelectedPerson = (shifts: IShiftPersonLineRecItem[]) => {
        const { lines, selected_person } = this.state;

        if (selected_person === null) {
            return;
        }

        const person_uuid = selected_person.uuid;
        const all_shifts_scheduled = shifts.every(shift => shift.persons.some(p => p.uuid === person_uuid));
        for (const shift of shifts) {
            const person_index = shift.persons.findIndex(p => p.uuid === person_uuid);
            if (!all_shifts_scheduled) {
                if (person_index < 0) {
                    shift.persons.push({
                        uuid: person_uuid,
                        name: selected_person.name,
                        id: selected_person.id,
                    });
                    shift.persons.sort((a, b) => a.name.localeCompare(b.name));
                }
            } else {
                if (person_index >= 0) {
                    shift.persons.splice(person_index, 1);
                }
            }
        }

        this.setState({
            lines: [...lines],
        });
    };

    handleUpdateSchedule = (lines: IShiftPersonLineRec[]) => {
        this.setState({
            lines,
        });
    };

    handleLineSelect = (selected_line: IShiftPersonLineRec | null) => {
        this.setState({
            selected_line,
        });
    };

    handlePersonSelect = (selected_person: PersonData | null) => {
        this.setState({
            selected_person,
        });
    };

    handlePersonScrollRequest = (personUuid: string) => {
        this.peopleRef.current?.scrollToPerson(personUuid);
    };

    handleShiftSelect = (selected_shift: string) => {
        this.setState({
            selected_shift,
            selected_shift_group: -1,
        });
    };

    handleShiftGroupSelect = (selected_shift_group: number) => {
        this.setState({
            selected_shift: "",
            selected_shift_group,
        });
    };

    handleSave = async () => {
        const { pooled_lines, onSave } = this.props;
        const { lines } = this.state;
        this.setState({ is_saving: true, error: "" });
        try {
            const updatedLines = unionBy(lines, pooled_lines, line => line.line_uuid);
            await onSave(updatedLines);
        } catch (error) {
            this.setState({
                error: translate("common.error_save", "An error occurred while saving data"),
            });
            console.error("Failed to save schedule:", error);
        } finally {
            this.setState({ is_saving: false });
        }
    };

    handleHideFullyScheduledPeopleChange = () => {
        this.setState(prev_state => ({
            hide_fully_scheduled_people: !prev_state.hide_fully_scheduled_people,
        }));
    };

    handleHidePartiallyScheduledPeopleChange = () => {
        this.setState(prev_state => ({
            hide_partially_scheduled_people: !prev_state.hide_partially_scheduled_people,
        }));
    };

    handleHideScheduledLinesChange = () => {
        this.setState(prev_state => ({
            hide_scheduled_lines: !prev_state.hide_scheduled_lines,
        }));
    };

    handleEditLineGroupsModalToggle = () => {
        this.setState(prev_state => ({
            show_edit_line_groups_modal: !prev_state.show_edit_line_groups_modal,
        }));
    };

    handleLineLayoutModalSaveSuccess = (sections: LineSection[]) => {
        this.setState({ lineSections: getSectionsViewModel(sections, this.state.lines) });
    };

    handlePersonLineScoresEditorHide = () => {
        this.setState({
            show_person_line_scores_modal: false,
        });
    };

    handlePersonLineScoresEditorShow = () => {
        this.setState({
            show_person_line_scores_modal: true,
        });
    };

    handleLoadTemplateModalToggle = () => {
        this.setState(prev_state => ({
            show_load_template_modal: !prev_state.show_load_template_modal,
        }));
    };

    handleTemplateApply = async (template: IPeopleScheduleTemplate) => {
        const { pool_line } = this.props;
        const { lines } = this.state;
        const line_shift_people_map: Map<string, string[][]> = new Map();
        for (const line of template.lines) {
            line_shift_people_map.set(line.line_uuid, line.shifts_people);
        }

        const shifts_pool_people: Set<string>[] = pool_line.shifts.map(
            shift => new Set(shift.persons.map(person => person.uuid))
        );

        for (const line of lines) {
            const line_people = line_shift_people_map.get(line.line_uuid);
            if (line_people !== undefined) {
                for (let i = 0; i < line_people.length; i++) {
                    const shift_pool_people = shifts_pool_people[i] || new Set();
                    const shift_people = line_people[i];
                    const shift = line.shifts[i];
                    if (!shift.enabled) {
                        continue;
                    }

                    const current_shift_people = new Set(shift.persons.map(person => person.uuid));
                    for (const person_uuid of shift_people) {
                        if (!current_shift_people.has(person_uuid) && shift_pool_people.has(person_uuid)) {
                            const person_details = this.getPersonDetails(person_uuid);
                            shift.persons.push({
                                uuid: person_uuid,
                                name: person_details !== undefined ? person_details.name : "",
                                id: person_details !== undefined ? person_details.id : 0,
                            });
                        }
                    }
                }
            }
        }

        this.setState({
            lines: [...lines],
        });
    };

    handleSaveAsTemplateModalToggle = () => {
        this.setState(prev_state => ({
            show_save_as_template_modal: !prev_state.show_save_as_template_modal,
        }));
    };

    handleSaveAsTemplate = async (name: string) => {
        const { line_group_uuid } = this.props;
        const { lines } = this.state;
        const template: IPeopleScheduleTemplate = {
            name,
            line_group_uuid,
            lines: [],
        };

        for (const line of lines) {
            const shifts_people: string[][] = [];
            let last_shift_index = -1;
            for (let i = 0; i < line.shifts.length; i++) {
                const shift = line.shifts[i];
                const shift_people = shift.persons.map(person => person.uuid);
                shifts_people.push(shift_people);
                if (shift_people.length > 0) {
                    last_shift_index = i;
                }
            }

            if (last_shift_index >= 0) {
                template.lines.push({
                    line_uuid: line.line_uuid,
                    shifts_people: shifts_people.slice(0, last_shift_index + 1),
                });
            }
        }

        await getBackend().manufacturing.updatePeopleScheduleTemplate({
            template,
        });
    };

    handlePrintClick = () => {
        this.setState({
            show_print_modal: true,
        });
    };

    handlePrintModalClose = () => {
        this.setState({
            show_print_modal: false,
        });
    };

    handlePrintModalPrint = async (options: PrintOptions) => {
        const { year, week, line_group_uuid, plant_uuid, people } = this.props;
        const { lineSections } = this.state;
        let logo = "";

        try {
            const org_code = Auth.getOrgData().code;
            const resp = (await getBackend().getApiClient().get(`/dynamic/logo/${org_code}/base64`)).data;
            logo = `data:${resp.mime};base64,${resp.data}`;
        } catch (error) {
            // Failed to load logo
        }

        const saved_settings = setNestedValue(this.state.saved_settings, [plant_uuid, line_group_uuid], {
            scope: options.scope,
            line_uuids: options.lines.map(line => line.line_uuid),
        });
        this.setState({ saved_settings });
        local_settings.setItem(FIELD_SHIFT_ASSIGNMENT_WEEK_POOL_PRINT, JSON.stringify(saved_settings));

        const html = renderToString(
            <IntlProviderWrapper>
                <PrintPage
                    year={year}
                    week={week}
                    plant_uuid={plant_uuid}
                    line_group_uuid={line_group_uuid}
                    lines={options.lines}
                    lineSections={lineSections}
                    logo={logo}
                    options={options}
                    people={people}
                    getHolidaysForDate={this.getHolidaysForDate}
                />
            </IntlProviderWrapper>
        );

        createHtmlAndDownload(html, "timetable-" + week + ".html");
    };

    handleAutomaticSchedulingParamsChange = (params: IPeopleSchedulingParams) => {
        this.setState(prev_state => {
            storePeopleSchedulingParams(FIELD_AUTOMATIC_SCHEDULING_PARAMS, params);
            return {
                ...prev_state,
                automatic_scheduling_params: params,
            };
        });
    };

    handleShowUnavailablePeopleChange = () => {
        this.setState(prev_state => ({
            show_unavailable_people: !prev_state.show_unavailable_people,
        }));
    };

    handlePinChange = async (person_uuid: string, line_pin_state: LinePinState) => {
        const pinned_line_uuids_set = this.getPersonPinnedLines(person_uuid);

        for (const line_uuid of Object.keys(line_pin_state)) {
            const pinned = line_pin_state[line_uuid];
            if (pinned) {
                pinned_line_uuids_set.add(line_uuid);
            } else {
                pinned_line_uuids_set.delete(line_uuid);
            }
        }

        const line_uuids = Array.from(pinned_line_uuids_set);

        this.setState({
            people_pinned_lines: [
                ...this.state.people_pinned_lines.filter(x => x.person_uuid !== person_uuid),
                {
                    person_uuid,
                    line_uuids,
                },
            ],
        });

        try {
            await getBackend().manufacturing.setPeopleLines({
                people_lines: [
                    {
                        person_uuid,
                        line_uuids,
                    },
                ],
            });
        } catch (error) {
            this.setState({
                error: translate("common.error_save", "An error occurred while saving data"),
            });
            console.error("Failed to update pinned lines:", error);
        }
    };

    handleError = (error: string) => {
        this.setState({
            error,
        });
    };

    handleCommentSave = async (
        comment: string,
        personId: number,
        slots: CommentSlotWithExistingId[]
    ): Promise<void> => {
        const { people, week, year } = this.props;

        const commentIdsToDelete: number[] = [];
        const commentUpdates: IPersonShiftCommentUpdate[] = [];
        const newComments: IPersonShiftComment[] = [];

        for (const slot of slots) {
            if (comment === "") {
                if (slot.existingId !== undefined) {
                    commentIdsToDelete.push(slot.existingId);
                }
            } else {
                if (slot.existingId !== undefined) {
                    commentUpdates.push({ comment_id: slot.existingId, comment });
                } else {
                    newComments.push({
                        person_id: personId,
                        date: slot.date,
                        shift_number_day: slot.shift ?? undefined,
                        line_id: slot.lineId ?? undefined,
                        comment,
                    });
                }
            }
        }

        let saveError: unknown = null;

        try {
            if (commentIdsToDelete.length > 0) {
                await getBackend().manufacturing.deletePeopleShiftsComments({ ids: commentIdsToDelete });
            }

            if (commentUpdates.length > 0) {
                await getBackend().manufacturing.updatePeopleShiftsComments({ comments: commentUpdates });
            }

            if (newComments.length > 0) {
                await getBackend().manufacturing.setPeopleShiftsComments({ comments: newComments });
            }
        } catch (error) {
            saveError = error;
        }

        try {
            const peopleComments = await fetchPeopleComments(
                people.map(p => p.id),
                week,
                year
            );
            this.setState({ peopleComments });
        } catch (refreshError) {
            // Keep the original save failure if there was one; otherwise surface refresh failure.
            if (saveError === null) {
                throw refreshError;
            }
        }

        if (saveError !== null) {
            throw saveError;
        }
    };

    handleWorkerExchangeModalToggle = () => {
        this.setState(
            prevState => ({
                showWorkerExchangeModal: !prevState.showWorkerExchangeModal,
                selectedWorkerExchangePersonId: null,
            }),
            () => {
                if (!this.state.showWorkerExchangeModal) {
                    this.props.onScheduleDataRefresh();
                    this.refreshWorkerExchangeData();
                }
            }
        );
    };

    handleWorkerExchangeOffer = (personId: number) => {
        this.setState(() => ({
            selectedWorkerExchangePersonId: personId,
            showWorkerExchangeModal: true,
        }));
    };

    /**
     * Gets the scores as a map queried by person_uuid or line_uuid.
     * If line_uuid provided, returns a map of person_uuid to HeatParams.
     * If person_uuid provided, returns a map of line_uuid to HeatParams.
     */
    getScoresByUuid = (
        person_line_scores: IPersonLineScore[],
        { person_uuid, line_uuid }: PersonLineScoresQuery
    ): Map<string, HeatParams> => {
        const getScores = (scores: IPersonLineScore[], uuid: string, uuidParamName: string) =>
            scores.filter(score => score[uuidParamName] === uuid);

        const reduceToMap = (scores: IPersonLineScore[], uuidParamName: string) =>
            new Map(scores.map(score => [score[uuidParamName], { dwell: score.dwell, warmup: score.warmup }]));

        if (line_uuid) {
            const scores = getScores(person_line_scores, line_uuid, "line_uuid");
            return reduceToMap(scores, "person_uuid");
        }

        if (person_uuid) {
            const scores = getScores(person_line_scores, person_uuid, "person_uuid");
            return reduceToMap(scores, "line_uuid");
        }

        return new Map();
    };

    render() {
        const {
            line_group_uuid,
            plant_uuid,
            pool_line,
            pooled_lines,
            getPersonCompetenceLevel,
            year,
            week,
            onWeekChange,
        } = this.props;
        const {
            error,
            lines,
            lineSections,
            selected_line,
            selected_person,
            selected_shift,
            selected_shift_group,
            selectedWorkerExchangePersonId,
            hide_fully_scheduled_people,
            hide_partially_scheduled_people,
            hide_scheduled_lines,
            show_unavailable_people,
            person_line_scores,
            show_person_line_scores_modal,
            show_load_template_modal,
            show_save_as_template_modal,
            show_edit_line_groups_modal,
            automatic_scheduling_params,
            showWorkerExchangeModal,
            saved_settings,
        } = this.state;

        const areControlsDisabled = this.isLoading() || this.isSaving();
        const lineGroupExternalId = getLineGroupForUser(line_group_uuid)?.external_id ?? "";

        return (
            <div className="shift-assignment-week-pool">
                {error !== "" && (
                    <ErrorComponent className="shift-assignment-week-pool__error" type="error" msg={error} />
                )}
                <div className="shift-assignment-week-pool__toolbar">
                    <div className="row align-items-center">
                        <div className="col col-auto">
                            <Selection
                                selected_line={selected_line}
                                selected_person={selected_person}
                                selected_shift={selected_shift}
                                selected_shift_group={selected_shift_group}
                                onLineSelect={this.handleLineSelect}
                                onPersonSelect={this.handlePersonSelect}
                                onShiftSelect={this.handleShiftSelect}
                                onShiftGroupSelect={this.handleShiftGroupSelect}
                            />
                        </div>
                        <div className="col col-auto ml-auto">
                            <div className="form-row">
                                <div className="col-auto">
                                    <WeekPicker
                                        year={year}
                                        week={week}
                                        disabled={areControlsDisabled}
                                        onChange={onWeekChange}
                                    />
                                </div>
                                {Auth.roleCanEditShiftBoard() && (
                                    <div className="col-auto">
                                        <button
                                            className="btn btn-primary"
                                            type="button"
                                            disabled={areControlsDisabled}
                                            onClick={this.handleSave}
                                        >
                                            <i className="fas fa-save mr-1" /> {translate("common.save", "Save")}{" "}
                                            {this.isSaving() && <i className="fas fa-spinner fa-spin ml-1" />}
                                        </button>
                                    </div>
                                )}
                                <div className="col-auto">
                                    <Settings
                                        disabled={areControlsDisabled}
                                        hide_fully_scheduled_people={hide_fully_scheduled_people}
                                        hide_partially_scheduled_people={hide_partially_scheduled_people}
                                        hide_scheduled_lines={hide_scheduled_lines}
                                        show_unavailable_people={show_unavailable_people}
                                        onHideFullyScheduledPeopleChange={this.handleHideFullyScheduledPeopleChange}
                                        onHidePartiallyScheduledPeopleChange={
                                            this.handleHidePartiallyScheduledPeopleChange
                                        }
                                        onHideScheduledLinesChange={this.handleHideScheduledLinesChange}
                                        onLoadTemplateClick={this.handleLoadTemplateModalToggle}
                                        onPrintClick={this.handlePrintClick}
                                        onSaveAsTemplateClick={this.handleSaveAsTemplateModalToggle}
                                        onEditLineGroupsClick={this.handleEditLineGroupsModalToggle}
                                        onShowPersonLineScoresEditorClick={this.handlePersonLineScoresEditorShow}
                                        onShowUnavailablePeopleChange={this.handleShowUnavailablePeopleChange}
                                        onWorkerExchangeClick={this.handleWorkerExchangeModalToggle}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {this.isLoading() ? (
                    <div className="pt-3">
                        <Loader />
                    </div>
                ) : (
                    <div className="shift-assignment-week-pool__container">
                        <People
                            ref={this.peopleRef}
                            lineGroupExternalId={lineGroupExternalId}
                            people={this.getAvailablePeopleDetails()}
                            people_stats={this.getScheduleStats().people}
                            selected_line={selected_line}
                            selected_person={selected_person}
                            selected_shift={selected_shift}
                            selected_shift_group={selected_shift_group}
                            person_line_scores={person_line_scores}
                            filterPerson={this.filterPerson}
                            getAvailableShifts={this.getAvailableShifts}
                            getPersonCompetenceLevel={getPersonCompetenceLevel}
                            getPersonPinnedLines={this.getPersonPinnedLines}
                            getExchangeListingInfo={this.getExchangeListingInfo}
                            getLineGroupDetails={this.getLineGroupDetails}
                            onPersonSelect={this.handlePersonSelect}
                            onPinChange={this.handlePinChange}
                            onWorkerExchangeOffer={this.handleWorkerExchangeOffer}
                            getScoresByUuid={this.getScoresByUuid}
                        />
                        <Schedule
                            line_group_uuid={line_group_uuid}
                            pool_line={pool_line}
                            pooled_lines={lines}
                            lineSections={lineSections}
                            person_line_scores={person_line_scores}
                            schedule_stats={this.getScheduleStats()}
                            selected_line={selected_line}
                            selected_person={selected_person}
                            selected_shift={selected_shift}
                            selected_shift_group={selected_shift_group}
                            automatic_scheduling_params={automatic_scheduling_params}
                            filterLine={this.filterLine}
                            absentPeopleByShiftTag={this.getAbsentPeopleDetailsByShiftTag()}
                            getHolidaysForDate={this.getHolidaysForDate}
                            getLineDetails={this.getLineDetails}
                            getPersonCompetenceLevel={getPersonCompetenceLevel}
                            getPersonDetails={this.getPersonDetails}
                            getPersonPinnedLines={this.getPersonPinnedLines}
                            getPersonComments={this.getPersonComments}
                            getExchangeListingInfo={this.getExchangeListingInfo}
                            onCommentSave={this.handleCommentSave}
                            onLineSelect={this.handleLineSelect}
                            onPersonRemoveFromShift={this.removePersonFromShift}
                            onPersonSelect={this.handlePersonSelect}
                            onPersonScrollRequest={this.handlePersonScrollRequest}
                            onPersonToggle={this.toggleSelectedPerson}
                            onShiftSelect={this.handleShiftSelect}
                            onShiftGroupSelect={this.handleShiftGroupSelect}
                            onUpdateSchedule={this.handleUpdateSchedule}
                            onAutomaticSchedulingParamsChange={this.handleAutomaticSchedulingParamsChange}
                            onPinChange={this.handlePinChange}
                            onError={this.handleError}
                            getScoresByUuid={this.getScoresByUuid}
                        />
                    </div>
                )}
                <PersonLineModal
                    show={show_person_line_scores_modal}
                    title={translate("ShiftAssignmentPoolEdit.scores", "Scores")}
                    getPersonLineItems={this.getPersonLineItems}
                    onHide={this.handlePersonLineScoresEditorHide}
                />
                <LoadTemplateModal
                    line_group_uuid={line_group_uuid}
                    show={show_load_template_modal}
                    onClose={this.handleLoadTemplateModalToggle}
                    onApply={this.handleTemplateApply}
                />
                <SaveAsTemplateModal
                    line_group_uuid={line_group_uuid}
                    show={show_save_as_template_modal}
                    onClose={this.handleSaveAsTemplateModalToggle}
                    onSave={this.handleSaveAsTemplate}
                />
                <PrintModal
                    show={this.state.show_print_modal}
                    week={week}
                    year={year}
                    pooled_lines={lines}
                    saved_settings={saved_settings?.[plant_uuid]?.[line_group_uuid]}
                    onClose={this.handlePrintModalClose}
                    onPrint={this.handlePrintModalPrint}
                />
                <LineLayoutModal
                    lineGroupUuid={line_group_uuid}
                    sections={lineSections}
                    show={show_edit_line_groups_modal}
                    onClose={this.handleEditLineGroupsModalToggle}
                    onSaveSuccess={this.handleLineLayoutModalSaveSuccess}
                />
                <WorkerExchangeModal
                    show={showWorkerExchangeModal}
                    originalSchedule={pooled_lines}
                    currentSchedule={lines}
                    onSave={this.handleSave}
                    onClose={this.handleWorkerExchangeModalToggle}
                >
                    <WorkerExchange
                        lineGroupUuid={line_group_uuid}
                        people={this.props.people}
                        peopleSchedule={pooled_lines}
                        poolLine={pool_line}
                        selectedPersonId={selectedWorkerExchangePersonId}
                        week={week}
                        year={year}
                    />
                </WorkerExchangeModal>
            </div>
        );
    }
}

export default ShiftAssignmentWeekPool;
